/**
 * Cart Editor V2 — curtarra.com
 * Handles product editing from the cart drawer.
 */

(() => {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function money(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /** PDP stores numeric width/length in cm for EU; pricing APIs expect inches (see product-variants-v11). */
  function cmToInchesForApi(num) {
    const n = parseFloat(num);
    if (isNaN(n)) return '';
    return (n * 0.393701).toFixed(1);
  }

  function decodeEntitiesBasic(str) {
    if (str === null || str === undefined) return str;
    let s = String(str);
    // Iteratively unwind any depth of HTML-entity escaping. Without `&amp;`
    // decoding here, an `&` in a cart line property gets one extra `amp;`
    // layer every time the user opens the editor and saves, producing
    // visible `&amp;amp;...` in the cart line summary.
    let prev;
    do {
      prev = s;
      s = s
        .replaceAll('&amp;', '&')
        .replaceAll('&quot;', '"')
        .replaceAll('&#34;', '"')
        .replaceAll('&apos;', "'")
        .replaceAll('&#39;', "'")
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>');
    } while (s !== prev);
    return s;
  }

  function showToast(msg, duration = 3200) {
    const el = $('#cdv2-toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('isvisible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('isvisible'), duration);
  }

  /* ── Icon SVG shortcuts ──────────────────────────────────── */
  const ICON = {
    minus: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    plus:  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    check: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="2.5 8 6.5 12 13.5 4"/></svg>',
  };

  /* ── Template → capability map ──────────────────────────── */
  const TEMPLATE_CAPS = {
    'custom':           { colors: true, headers: true, linings: true, dimensions: true, room: true, design: true },
    'custom-door':      { colors: true, headers: true, linings: true, dimensions: true, room: true, design: false },
    'custom-cafe':      { colors: true, headers: true, linings: true, dimensions: true, room: true, design: false },
    'custom-valance':   { colors: true, headers: true, mounts: true, linings: true, dimensions: true, room: true, design: true },
    'custom-swag':      { colors: true, headers: true, mounts: true, linings: false, dimensions: true, room: true, design: true },
    'standard':         { colors: true, headers: false, linings: false, dimensions: false, room: false, design: false },
    'pillow-cover':     { colors: true, headers: false, linings: false, dimensions: false, room: false, fabric: true, design: false },
    'shades':           { colors: true, headers: false, linings: false, dimensions: true, mounts: true, controls: true, room: true, design: false },
    /* Custom-pattern shades: nothing is editable here. The saved artwork preview
       is composited against a specific W:L ratio AND fabric colour, so changing
       either would leave the line item carrying a preview that contradicts the
       order. cart-drawer.liquid already withholds the Edit button; this entry
       keeps the guard real if that ever changes — note TEMPLATE_CAPS.standard
       would still have allowed `colors`, which is one of the two harmful edits. */
    'roller-shades-pattern': { colors: false, headers: false, linings: false, dimensions: false, mounts: false, controls: false, room: false, design: false },
    'custom-track':     { colors: true, headers: false, linings: false, dimensions: true, room: true, design: false },
    'custom-track-joe': { colors: true, headers: false, linings: false, dimensions: true, room: true, design: false },
    'custom-track-bend':{ colors: true, headers: false, linings: false, dimensions: true, room: true, bend_note: true, design: false },
    'custom-track-rex': { colors: true, headers: false, linings: false, dimensions: true, room: true, design: false },
    'custom-track-roman':{ colors: true, headers: false, linings: false, dimensions: true, room: true, design: false },
    'custom-track-u':   { colors: true, headers: false, linings: false, dimensions: true, room: true, design: false },
  };

  function resolveTemplateCaps(tmpl) {
    if (!tmpl) return TEMPLATE_CAPS.standard;
    if (TEMPLATE_CAPS[tmpl]) return TEMPLATE_CAPS[tmpl];
    // NB: the exact-match lookup above already catches 'roller-shades-pattern',
    // which must not fall through to the shades caps below.
    if (tmpl.includes('shades')) return TEMPLATE_CAPS.shades;
    if (tmpl.includes('custom-track')) return TEMPLATE_CAPS['custom-track'];
    if (tmpl === 'track-backup') return TEMPLATE_CAPS['custom-track'];
    return TEMPLATE_CAPS.standard;
  }

  // getEditorFieldOrder removed — logic moved into CartEditor._renderFieldsForTemplate

  /* ══════════════════════════════════════════════════════════════════════
     CUSTOM CURTAIN PRICING — delegated to window.PriceCalc (see snippets/
     price-calculator.liquid). These thin wrappers preserve the original
     cart-editor.js function signatures so existing call sites stay simple.
  ══════════════════════════════════════════════════════════════════════ */

  function _curtainCalcSize(w, h, params, tmpl) {
    return window.PriceCalc.calcSize(w, h, params, tmpl);
  }

  function _curtainCalcLining(w, h, paramHeader, paramLining, tmpl) {
    // Returns USD (no exchangeRate), rounded to a whole dollar to match the amount the
    // cart function charges (run.rs CustomCurtain::get_lining).
    const per = parseFloat(tmpl.per);
    let multiplier = 1;
    const base = (paramLining * w) / 35;
    if (h > 104) multiplier = Math.ceil((h * 2) / 104) / 2;
    return Math.round(base * multiplier * (1 + paramHeader / 100) * per);
  }

  function _curtainCalcDesign(w, h, designKey, paramHeader, paramDesign) {
    return window.PriceCalc.calcDesign(w, h, designKey, paramHeader, paramDesign);
  }

  function _curtainCalcTrim(w, h, paramHeader, trimType, basePrice, per) {
    return window.PriceCalc.calcTrimPrice(w, h, paramHeader, trimType, basePrice, per);
  }

  function _curtainCalcFinalPrice(curtainSize, paramHeader, liningPrice, trimPrice, designPrice, exchangeRate, discountRate) {
    const base = curtainSize * (1 + paramHeader/100) + liningPrice + trimPrice + designPrice;
    return parseFloat((base * exchangeRate * discountRate).toFixed(2));
  }

  /* ── Size validation (mirrors PDP: product-custom-variants / v5 / v3) ── */

  function isDimCm(data, props) {
    return data?.size_unit === 'cm' || props?._cm === '1';
  }

  const FRACTION_OPTIONS = ['0/0', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'];

  /** Shades inches: whole inches + eighths select (PDP v5). */
  function usesShadeFractionSelects(data, props) {
    return data?.size_validation === 'range' && !isDimCm(data, props);
  }

  /** custom-track-joe inches: width + eighths select (PDP v3). */
  function usesTrackJoeFractionSelects(data, props) {
    return data?.template === 'custom-track-joe' && !isDimCm(data, props);
  }

  /** Custom / cm shades / header_min: one decimal place allowed. */
  function dimAllowsDecimals(data, props) {
    const mode = data?.size_validation || 'none';
    if (mode === 'header_min' || mode === 'none') return true;
    if (mode === 'range' && isDimCm(data, props)) return true;
    return false;
  }

  function dimIntegersOnly(data, props) {
    return !dimAllowsDecimals(data, props);
  }

  function fractionPropKey(dim) {
    return dim === 'width' ? '_minwidth' : '_minlength';
  }

  function getFractionSelectValue(props, dim) {
    const key = fractionPropKey(dim);
    const v = props[key];
    if (v == null || v === '' || v === '0') return '0/0';
    return String(v);
  }

  function metaFieldNumber(obj, key) {
    if (!obj) return 0;
    const v = obj[key];
    if (v == null || v === '') return 0;
    if (typeof v === 'object' && v !== null && 'value' in v) return Number(v.value) || 0;
    return Number(v) || 0;
  }

  const FRACTION_VALUES = {
    '0/0': 0, '1/8': 0.125, '1/4': 0.25, '3/8': 0.375,
    '1/2': 0.5, '5/8': 0.625, '3/4': 0.75, '7/8': 0.875,
  };

  function getFractionValue(option) {
    if (option == null || option === '') return 0;
    return FRACTION_VALUES[String(option)] ?? 0;
  }

  /** For cm→inch conversion: find the nearest eighths fraction for a decimal part. */
  function nearestEighthsFraction(decimalPart) {
    let best = '0/0';
    let minDiff = Infinity;
    for (const [opt, val] of Object.entries(FRACTION_VALUES)) {
      const diff = Math.abs(decimalPart - val);
      if (diff < minDiff) { minDiff = diff; best = opt; }
    }
    return best;
  }

  /** Convert cm user input to inch storage values for shades (range mode). */
  function cmToInchStorage(cmVal) {
    const totalInches = cmVal * 0.393701;
    const intPart = Math.floor(totalInches);
    const frac = nearestEighthsFraction(totalInches - intPart);
    return { intPart, frac, totalInches: intPart + FRACTION_VALUES[frac] };
  }

  function normalizeRangeList(list) {
    if (list == null || list === '') return [];
    if (typeof list === 'string') {
      const s = list.trim();
      if (!s) return [];
      if (s.startsWith('[')) {
        try {
          const parsed = JSON.parse(s);
          return normalizeRangeList(parsed);
        } catch (e) { /* fall through */ }
      }
      return s.replace(/[\[\]"]/g, '').split(',').map((r) => r.trim()).filter(Boolean);
    }
    const arr = Array.isArray(list) ? list : [list];
    return arr.map((r) => String(r).trim()).filter(Boolean);
  }

  function metaobjectDisplayName(entry) {
    if (!entry) return '';
    const n = entry.name;
    if (typeof n === 'string') return n;
    if (n && typeof n === 'object') return n.value ?? n.text ?? '';
    return entry.system?.handle || entry.handle || '';
  }

  /** Shades PDP validates _maxwidth + fraction; cart line stores both. */
  function parseMinwidthFromProps(props) {
    const mw = props._minwidth ?? props['_minwidth'];
    if (mw != null && mw !== '' && mw !== '0' && mw !== '0/0') {
      if (String(mw).includes('/')) return getFractionValue(mw);
      return Number(mw) || 0;
    }
    const wStr = props.Width || props['Track Width'] || '';
    const m = String(wStr).match(/^(\d+(?:\.\d+)?)\s+(\d+\/\d+)/);
    if (m) return getFractionValue(m[2]);
    return 0;
  }

  function parseMinlengthFromProps(props) {
    const ml = props._minlength ?? props['_minlength'];
    if (ml != null && ml !== '' && ml !== '0' && ml !== '0/0') {
      if (String(ml).includes('/')) return getFractionValue(ml);
      return Number(ml) || 0;
    }
    const lStr = props.Length || '';
    const m = String(lStr).match(/^(\d+(?:\.\d+)?)\s+(\d+\/\d+)/);
    if (m) return getFractionValue(m[2]);
    return 0;
  }

  function getWidthForRangeCheck(props, data) {
    if (data?.size_validation === 'range') {
      if (isDimCm(data, props)) {
        // cm: range comparisons are in cm; extract display cm value from Width prop
        const m = String(props.Width || '').match(/^(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : NaN;
      }
      const maxw = props._maxwidth;
      if (maxw !== '' && maxw != null && !Number.isNaN(Number(maxw))) {
        return Number(maxw) + parseMinwidthFromProps(props);
      }
    }
    // track_range + cm: _track_width stores inches but range is in cm; extract display cm value
    if (data?.size_validation === 'track_range' && isDimCm(data, props)) {
      const wDisp = props['Track Width'] || props['Rod Width'] || '';
      const m = String(wDisp).match(/^(\d+(?:\.\d+)?)/);
      if (m) return parseFloat(m[1]);
    }
    const raw = props._track_width ?? props._width;
    return raw === '' || raw == null ? NaN : Number(raw);
  }

  function getEditableWidthValue(props, data) {
    if (isDimCm(data, props)) {
      // cm products: show the cm value extracted from Width/Track Width/Rod Width display prop
      const wDisp = props.Width || props['Track Width'] || props['Rod Width'];
      if (wDisp) {
        const m = String(wDisp).match(/^(\d+(?:\.\d+)?)/);
        if (m) return parseFloat(m[1]);
      }
      return '';
    }
    if (data?.size_validation === 'range') {
      const maxw = props._maxwidth;
      if (maxw !== '' && maxw != null && !Number.isNaN(Number(maxw))) {
        return Number(maxw);
      }
      const total = props._width;
      if (total !== '' && total != null && !Number.isNaN(Number(total))) {
        return Number(total) - parseMinwidthFromProps(props);
      }
    }
    // Track-joe inch: integer part is in _maxwidth (fraction is separate)
    if (data?.template === 'custom-track-joe' && !isDimCm(data, props)) {
      const mw = props._maxwidth;
      if (mw !== '' && mw != null && !Number.isNaN(Number(mw))) return Number(mw);
    }
    const raw = props._track_width ?? props._width ?? props.Width;
    if (raw === '' || raw == null) return '';
    return parseFloat(String(raw).replace(/[^\d.]/g, '')) || '';
  }

  function getLengthForRangeCheck(props, data) {
    if (data?.size_validation === 'range') {
      if (isDimCm(data, props)) {
        // cm: extract display cm value from Length prop
        const m = String(props.Length || '').match(/^(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : NaN;
      }
      const maxl = props._maxlength;
      if (maxl !== '' && maxl != null && !Number.isNaN(Number(maxl))) {
        return Number(maxl) + parseMinlengthFromProps(props);
      }
    }
    const raw = props._length;
    return raw === '' || raw == null ? NaN : Number(raw);
  }

  function getEditableLengthValue(props, data) {
    if (isDimCm(data, props)) {
      // cm products: show the cm value extracted from Length display prop
      const lDisp = props.Length;
      if (lDisp) {
        const m = String(lDisp).match(/^(\d+(?:\.\d+)?)/);
        if (m) return parseFloat(m[1]);
      }
      return '';
    }
    if (data?.size_validation === 'range') {
      const maxl = props._maxlength;
      if (maxl !== '' && maxl != null && !Number.isNaN(Number(maxl))) {
        return Number(maxl);
      }
      const total = props._length;
      if (total !== '' && total != null && !Number.isNaN(Number(total))) {
        return Number(total) - parseMinlengthFromProps(props);
      }
    }
    const raw = props._length ?? props.Length;
    if (raw === '' || raw == null) return '';
    return parseFloat(String(raw).replace(/[^\d.]/g, '')) || '';
  }

  /** Preserve _maxwidth/_maxlength from cart line props (do not overwrite from Width display). */
  function normalizeDimensionPropsOnOpen(props, data) {
    if (!data?.has_dimensions) return;

    const mode = data.size_validation || 'none';
    const tmpl = data.template || '';
    const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';

    if (mode === 'range') {
      if (props._maxwidth === '' || props._maxwidth == null) {
        if (props._width !== '' && props._width != null && !Number.isNaN(Number(props._width))) {
          props._maxwidth = Number(props._width) - parseMinwidthFromProps(props);
        } else {
          const m = String(props.Width || '').match(/^(\d+(?:\.\d+)?)/);
          if (m) props._maxwidth = parseFloat(m[1]);
        }
      } else {
        props._maxwidth = Number(props._maxwidth);
      }

      if (!isTrack) {
        if (props._maxlength === '' || props._maxlength == null) {
          if (props._length !== '' && props._length != null && !Number.isNaN(Number(props._length))) {
            props._maxlength = Number(props._length) - parseMinlengthFromProps(props);
          } else {
            const m = String(props.Length || '').match(/^(\d+(?:\.\d+)?)/);
            if (m) props._maxlength = parseFloat(m[1]);
          }
        } else {
          props._maxlength = Number(props._maxlength);
        }
      }

      if (props._width !== '' && props._width != null) props._width = Number(props._width);
      if (!isTrack && props._length !== '' && props._length != null) props._length = Number(props._length);
      return;
    }

    if (isTrack) {
      const tw = props._track_width ?? props._width;
      if (tw !== '' && tw != null) {
        const n = Number(tw);
        if (!Number.isNaN(n)) props._track_width = n;
      }
      delete props._width;
      delete props.Width;

      // Track-joe inch: extract integer+fraction from _track_width for separate controls
      if (tmpl === 'custom-track-joe' && !isDimCm(data, props)) {
        const totalIn = Number(props._track_width);
        if (!Number.isNaN(totalIn)) {
          const intPart = Math.floor(totalIn);
          // If _minwidth is already a valid fraction (set by PDP), preserve it;
          // only re-derive when _track_width itself carries a decimal part.
          const existingMin = String(props._minwidth ?? '');
          const hasFracInTotal = (totalIn - intPart) > 0.001;
          let fracPart;
          if (!hasFracInTotal && existingMin && existingMin !== '0' && FRACTION_OPTIONS.includes(existingMin)) {
            fracPart = existingMin;
          } else {
            fracPart = nearestEighthsFraction(totalIn - intPart);
          }
          props._maxwidth = intPart;
          props._minwidth = fracPart === '0/0' ? '0' : fracPart;
          props['Track Width'] = fracPart !== '0/0' ? `${intPart} ${fracPart}"` : `${intPart}"`;
        }
        delete props['Rod Width'];
        return;
      }

      // Remove stale display fields that don't belong to this template
      if (tmpl === 'custom-track-roman') {
        delete props['Track Width'];
      } else if (tmpl !== 'custom-track-rex') {
        delete props['Rod Width'];
      }
      return;
    }

    const toNum = (v) => {
      if (v === '' || v == null) return null;
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.]/g, ''));
      return Number.isNaN(n) ? null : n;
    };

    const wn = toNum(props._width);
    if (wn != null) props._width = wn;
    else {
      const fromDisp = toNum(props.Width);
      if (fromDisp != null) props._width = fromDisp;
    }

    const ln = toNum(props._length);
    if (ln != null) props._length = ln;
    else {
      const fromDisp = toNum(props.Length);
      if (fromDisp != null) props._length = fromDisp;
    }
  }

  function isInRange(num, rangeStr) {
    if (rangeStr == null || rangeStr === '') return true;
    const parts = String(rangeStr).split('-').map(Number);
    if (parts.length !== 2 || parts.some((n) => Number.isNaN(n))) return true;
    return num >= parts[0] && num <= parts[1];
  }

  function rangeForUnit(rangeStr, isCm) {
    if (!rangeStr) return '';
    if (!isCm) return String(rangeStr);
    return String(rangeStr)
      .split('-')
      .map((i) => (Number(i) / 0.393701).toFixed(1))
      .join('-');
  }

  function findHeaderSizeRule(headerTitle, rules) {
    if (!headerTitle || !rules?.length) return null;
    return rules.find((r) => {
      const name = metaobjectDisplayName(r);
      return name && headerTitle.includes(name);
    }) || null;
  }

  /** Preserve partial decimals while typing (custom / cm). */
  function sanitizeDecimalInput(raw) {
    let str = String(raw ?? '').trim();
    if (str === '') return '';
    str = str.replace(/[^\d.]/g, '');
    const parts = str.split('.');
    if (parts.length > 2) str = `${parts[0]}.${parts.slice(1).join('')}`;
    if (parts.length === 2 && parts[1].length > 1) {
      str = `${parts[0]}.${parts[1].charAt(0)}`;
    }
    return str;
  }

  function sanitizeDimInput(raw, data, props) {
    const str = raw === null || raw === undefined ? '' : String(raw);
    if (str.trim() === '') return '';

    if (dimAllowsDecimals(data, props)) {
      return sanitizeDecimalInput(str);
    }

    const digits = str.replace(/[^0-9]/g, '');
    return digits === '' ? '' : parseInt(digits, 10);
  }

  /**
   * 卷帘「Ordered / Finished」四个可见属性。必须和 PDP 的
   * orderedWidthText / orderedLengthText / finishedWidthText / finishedLengthText
   * 保持同一口径 —— 一边改了另一边不改，客户在购物车动过尺寸后工厂就会读到陈旧值。
   *
   * Ordered 直接复用 props.Width / props.Length：它们已经是成型的展示串
   * （`35 7/8"` 或 `91.4cm`），和 PDP 输出逐字相同，不重新拼避免两套取整。
   * Finished 只有内装才有；外装必须 delete，否则把 Inside 改成 Outside 之后
   * 会留下一个不存在的减宽值。
   */
  function inchTextFromDecimal(dec) {
    // 与 PDP cfgInchText 同逻辑：向下取整 + 最近的八分之一
    const i = Math.floor(dec);
    const f = nearestEighthsFraction(dec - i);
    return f === '0/0' ? String(i) : `${i} ${f}`;
  }

  function syncShadeOrderedFinished(props, data) {
    const tmpl = data?.template || '';
    if (!tmpl.includes('shades')) return;

    const clearAll = () => {
      delete props['Ordered Width']; delete props['Ordered Length'];
      delete props['Finished Width']; delete props['Finished Length'];
    };
    const w = props.Width, l = props.Length;
    if (!w || !l) { clearAll(); return; }

    // 四项都是内装专属。外装不减宽，Ordered Width 就等于既有的 Width，
    // 留着只会让购物车/订单上同一个数字出现两个名字；而且 Inside→Outside 改过来
    // 之后若只删 Finished、留下 Ordered，工厂单上就是一半新口径一半旧口径。
    const mount = String(props['Mount Type'] || props['_mount_type'] || props['_mount'] || '').toLowerCase();
    if (!mount.includes('inside')) { clearAll(); return; }

    props['Ordered Width'] = w;
    props['Ordered Length'] = l;

    const isCm = isDimCm(data, props);
    let fw = null;
    if (isCm) {
      // props.Width 形如 `91.4cm`；cm 一律 1 位小数（与 PDP fmtCm 同口径）
      const cm = parseFloat(String(w));
      if (Number.isFinite(cm)) {
        const v = Math.round((cm - 0.25 / 0.393701) * 10) / 10;
        if (v > 0) fw = `${v}cm`;
      }
    } else {
      // props._width 是总英寸（整数 + 分数已合并）
      const total = parseFloat(props._width);
      if (Number.isFinite(total)) {
        const v = total - 0.25;
        if (v > 0) fw = `${inchTextFromDecimal(v)}"`;
      }
    }
    if (fw) {
      props['Finished Width'] = fw;
      props['Finished Length'] = l;   // 成品高度 = 下单高度，不减
    } else {
      // 成品宽算不出来（负数/NaN）时四项一起清掉，不留半套
      clearAll();
    }
  }

  function syncDimensionProps(props, data, dim, numVal) {
    const isCm = isDimCm(data, props);
    const tmpl = data.template || '';
    const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';
    const isRange = data.size_validation === 'range';

    if (dim === 'width') {
      if (isTrack && tmpl === 'custom-track-joe' && !isCm) {
        // Track-joe inch: integer + Eighths fraction selector
        const frac = getFractionSelectValue(props, 'width');
        props._maxwidth = numVal;
        props._minwidth = frac === '0/0' ? '0' : frac;
        props._track_width = numVal + getFractionValue(frac);
        props['Track Width'] = frac !== '0/0' ? `${numVal} ${frac}"` : `${numVal}"`;
        delete props['Rod Width'];
        delete props._width;
        delete props.Width;
      } else if (isTrack) {
        // Track: only _track_width + one template-specific display field
        // v3 family rounds to integer (mirrors PDP v3 buildProperties toFixed(0)); rex (v6) keeps 1 decimal
        const totalInches = isCm
          ? (tmpl === 'custom-track-rex' ? parseFloat((numVal * 0.393701).toFixed(1)) : Math.round(numVal * 0.393701))
          : numVal;
        props._track_width = totalInches;
        const dispVal = isCm ? `${numVal}cm` : `${numVal}"`;
        if (tmpl === 'custom-track-roman') {
          props['Rod Width'] = dispVal;
          delete props['Track Width'];
        } else {
          props['Track Width'] = dispVal;
          delete props['Rod Width'];
        }
        delete props._width;
        delete props.Width;
      } else if (isRange) {
        if (isCm) {
          // numVal is in cm; convert to inches for storage (mirrors shades PDP buildProperties)
          const { intPart, frac, totalInches } = cmToInchStorage(numVal);
          props._maxwidth = intPart;
          props._minwidth = frac === '0/0' ? '0' : frac;
          props._width = totalInches;
          props.Width = `${numVal}cm`;
        } else {
          const mw = getFractionSelectValue(props, 'width');
          props._maxwidth = numVal;
          props._width = numVal + getFractionValue(mw);
          props._minwidth = mw === '0/0' ? '0' : mw;
          props.Width = mw !== '0/0' ? `${numVal} ${mw}"` : `${numVal}"`;
        }
      } else {
        if (isCm) {
          // numVal is in cm; store inches (mirrors custom family PDP buildProperties)
          props._width = parseFloat((numVal * 0.393701).toFixed(1));
          props.Width = `${numVal}cm`;
        } else {
          props._width = numVal;
          props.Width = `${numVal}"`;
        }
      }
    } else if (dim === 'length') {
      if (isRange) {
        if (isCm) {
          const { intPart, frac, totalInches } = cmToInchStorage(numVal);
          props._maxlength = intPart;
          props._minlength = frac === '0/0' ? '0' : frac;
          props._length = totalInches;
          props.Length = `${numVal}cm`;
        } else {
          const ml = getFractionSelectValue(props, 'length');
          props._maxlength = numVal;
          props._length = numVal + getFractionValue(ml);
          props._minlength = ml === '0/0' ? '0' : ml;
          props.Length = ml !== '0/0' ? `${numVal} ${ml}"` : `${numVal}"`;
        }
      } else {
        if (isCm) {
          props._length = parseFloat((numVal * 0.393701).toFixed(1));
          props.Length = `${numVal}cm`;
        } else {
          props._length = numVal;
          props.Length = `${numVal}"`;
        }
      }
    }

    // 尺寸一变，卷帘的 Ordered/Finished 四项必须跟着重算，否则留下陈旧值
    syncShadeOrderedFinished(props, data);
  }

  /* ── Rex (v6 custom-track-rex) width type helpers ──────────────── */

  function isRexTrack(data) {
    return data?.width_types && data.width_types.length > 0;
  }

  /** Derive dimension count (1/2/3) from Width Type display title. */
  function getRexWidthTypeIndex(title) {
    if (!title) return 1;
    if (/bay/i.test(title)) return 3;
    if (/corner/i.test(title)) return 2;
    return 1;
  }

  /** Recompute _track_width and Rod Width display after X/Y/Z input changes.
   *  cmVals: optional { x, y, z } direct cm values from user input — avoids inch round-trip error. */
  function syncRexDimProps(props, isCm, cmVals) {
    const xIn = parseFloat(props._x) || 0;
    const yIn = parseFloat(props._y) || 0;
    const zIn = parseFloat(props._z) || 0;
    const totalIn = xIn + yIn + zIn;
    props._track_width = parseFloat(totalIn.toFixed(1));
    delete props._width; // rex uses _track_width only

    const inToDisp = (n, key) => {
      if (isCm && cmVals != null && cmVals[key] != null) return cmVals[key];
      const d = isCm ? n / 0.393701 : n;
      return parseFloat(d.toFixed(1));
    };
    const unit = isCm ? 'cm' : '"';
    const idx = getRexWidthTypeIndex(props['Width Type'] || '');

    const xD = inToDisp(xIn, 'x');
    const yD = inToDisp(yIn, 'y');
    const zD = inToDisp(zIn, 'z');
    const totalD = isCm ? parseFloat((xD + yD + zD).toFixed(1)) : parseFloat(totalIn.toFixed(1));

    if (idx === 1) {
      props['Rod Width'] = `${xD}${unit}`;
    } else if (idx === 2) {
      props['Rod Width'] = `${totalD}${unit} (X=${xD}, Y=${yD})`;
    } else {
      props['Rod Width'] = `${totalD}${unit} (X=${xD}, Y=${yD}, Z=${zD})`;
    }
  }

  /** Collect current cm values from XYZ form inputs. */
  function collectCmVals(form) {
    const cv = {};
    form.querySelectorAll('[data-dim-xyz]').forEach(inp => {
      const k = inp.dataset.dimXyz;
      const v = parseFloat(inp.value);
      if (!Number.isNaN(v) && v > 0) cv[k] = v;
    });
    return cv;
  }

  /** Decimal dimension input: clamps to 1 decimal place, syncs props. */
  function applyPdpValidateNumber(input, props, dim, data) {
    const sanitized = sanitizeDecimalInput(input.value);
    // Correct the displayed value so the user sees the enforced format
    if (input.value !== sanitized) input.value = sanitized;

    if (sanitized === '') {
      const propKey = dim === 'width' ? '_width' : '_length';
      delete props[propKey];
      if (dim === 'width') {
        delete props.Width;
        delete props._track_width;
        delete props['Track Width'];
        delete props['Rod Width'];
      } else {
        delete props.Length;
      }
      return;
    }

    syncDimensionProps(props, data, dim, Number(sanitized));
  }

  function applyFractionChange(props, data, dim, fracVal) {
    const key = fractionPropKey(dim);
    const stored = fracVal === '0/0' ? '0' : fracVal;
    props[key] = stored;

    const maxKey = dim === 'width' ? '_maxwidth' : '_maxlength';
    const maxVal = props[maxKey];
    if (maxVal === '' || maxVal == null) return;

    const numVal = Number(maxVal);
    if (Number.isNaN(numVal)) return;
    syncDimensionProps(props, data, dim, numVal);
  }

  function getActiveSizeRanges(data, props) {
    const lift = props?._lift_control || data?.lift_control || '';
    if (lift === 'cordless') {
      const c = normalizeRangeList(data?.cordless_size_input_range);
      if (c.length) return c;
    }
    if (lift === 'motorized') {
      const m = normalizeRangeList(data?.motorized_size_input_range);
      if (m.length) return m;
    }
    return normalizeRangeList(data?.size_input_range);
  }

  function rangeErrorMsg(rangeStr, mode) {
    if (mode === 'track_range') return `Please enter an integer between ${rangeStr}.`;
    return `The input range is ${rangeStr}.`;
  }

  function validateDimensionsForEditor(data, props) {
    if (!data?.has_dimensions) {
      return { width: '', length: '', valid: true };
    }

    const isCm = isDimCm(data, props);
    const mode = data.size_validation || 'none';
    const tmpl = data.template || '';
    const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';
    const wDisplay = getEditableWidthValue(props, data);
    const lDisplay = getEditableLengthValue(props, data);
    const ranges = getActiveSizeRanges(data, props);

    let widthErr = '';
    let lengthErr = '';

    if (wDisplay === '' || wDisplay === null || wDisplay === undefined) {
      widthErr = 'Required';
    }
    if (!isTrack && (lDisplay === '' || lDisplay === null || lDisplay === undefined)) {
      lengthErr = 'Required';
    }

    // _width/_length store inches; for header_min cm comparisons, convert inches→cm
    const wForHeader = (() => {
      const raw = props._track_width ?? props._width;
      if (raw === '' || raw == null) return NaN;
      const num = Number(raw);
      return isCm ? parseFloat((num / 0.393701).toFixed(1)) : num;
    })();
    const lForHeader = (() => {
      const raw = props._length;
      if (raw === '' || raw == null) return NaN;
      const num = Number(raw);
      return isCm ? parseFloat((num / 0.393701).toFixed(1)) : num;
    })();

    const wForRange = getWidthForRangeCheck(props, data);
    const lForRange = getLengthForRangeCheck(props, data);

    const rule = data.header_size_rule;
    if (rule && mode === 'header_min' && !Number.isNaN(wForHeader)) {
      let min = metaFieldNumber(rule, 'minimum_size');
      if (isCm && min) min = Number((min / 0.393701).toFixed(1));
      if (min > 0 && wForHeader < min) widthErr = `No less than ${min}.`;
    }
    if (rule && mode === 'header_min' && !isTrack && !Number.isNaN(lForHeader)) {
      let minL = metaFieldNumber(rule, 'height_minimum_size');
      if (isCm && minL) minL = Number((minL / 0.393701).toFixed(1));
      if (minL > 0 && lForHeader < minL) lengthErr = `No less than ${minL}.`;
    }

    if ((mode === 'range' || mode === 'track_range') && !Number.isNaN(wForRange) && ranges[0]) {
      const rangeStr = rangeForUnit(ranges[0], isCm);
      if (!isInRange(wForRange, rangeStr)) {
        widthErr = rangeErrorMsg(rangeStr, mode === 'track_range' ? 'track_range' : 'range');
      }
    }
    if (mode === 'range' && !isTrack && !Number.isNaN(lForRange) && ranges[1]) {
      const rangeStr = rangeForUnit(ranges[1], isCm);
      if (!isInRange(lForRange, rangeStr)) {
        lengthErr = rangeErrorMsg(rangeStr, 'range');
      }
    }

    // Rex (custom-track-rex): Width Type + required X/Y/Z check
    if (!widthErr && isRexTrack(data)) {
      if (!props._width_type) {
        widthErr = 'Please select a width type';
      } else {
        const rexIdx = getRexWidthTypeIndex(props['Width Type'] || '');
        if (!props._x || parseFloat(props._x) <= 0) {
          widthErr = 'X value is required';
        } else if (rexIdx >= 2 && (!props._y || parseFloat(props._y) <= 0)) {
          widthErr = 'Y value is required';
        } else if (rexIdx >= 3 && (!props._z || parseFloat(props._z) <= 0)) {
          widthErr = 'Z value is required';
        }
      }
    }

    const valid = !widthErr && !lengthErr;
    return { width: widthErr, length: lengthErr, valid };
  }

  /* ── Cart drawer detail panel helpers ─────────────────────── */
  function toggleCdv2ItemDetails(toggle) {
    const item = toggle.closest('.cdv2-item');
    if (!item) return;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));
    const panel = item.querySelector('.cdv2-detail-panel');
    if (panel) panel.classList.toggle('is-open', !isOpen);
  }

  function expandCdv2ItemDetails(itemEl) {
    const toggle = itemEl.querySelector('.cdv2-detail-toggle');
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', 'true');
    const panel = itemEl.querySelector('.cdv2-detail-panel');
    if (panel) panel.classList.add('is-open');
    requestAnimationFrame(() => {
      itemEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  function findCdv2ItemByKey(lineKey) {
    if (!lineKey) return null;
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return null;
    return [...drawer.querySelectorAll('.cdv2-item[data-item-key]')].find(
      (el) => el.dataset.itemKey === lineKey
    ) || null;
  }

  let cartEditorDocEventsBound = false;

  /** Show more — single capture listener so repeated inits / cart refresh cannot double-toggle. */
  function setupCartDrawerDetailToggles() {
    if (window.__cdv2DetailToggleBound) return;
    window.__cdv2DetailToggleBound = true;
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.cart-drawer-v2')) return;
      const tog = e.target.closest('.cdv2-detail-toggle');
      if (!tog) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      toggleCdv2ItemDetails(tog);
    }, true);
  }

  function bindCartEditorDocumentEvents() {
    if (cartEditorDocEventsBound) return;
    cartEditorDocEventsBound = true;

    setupCartDrawerDetailToggles();

    document.addEventListener('click', (e) => {
      if (e.target.closest('#cdv2-cart-lock')) {
        showToast('Save or go back to cart before editing the drawer.');
        return;
      }

      const btn = e.target.closest('.cdv2-edit-btn');
      if (btn) {
        e.preventDefault();
        const itemEl = btn.closest('.cdv2-item');
        if (itemEl && window.cartEditorV2) window.cartEditorV2.open(itemEl);
        return;
      }

      // Close panel when clicking outside the note area
      if (!e.target.closest('.cdv2-note')) {
        const openNote = document.querySelector('.cart-drawer-v2 .cdv2-note[data-open="true"]');
        if (openNote) {
          openNote.dataset.open = 'false';
          const tog = document.getElementById('order-note-toggle');
          if (tog) tog.setAttribute('aria-expanded', 'false');
        }
      }

      if (e.target.closest('.cdv2-note-save')) {
        window.cartEditorV2?._saveNote();
        return;
      }

      if (e.target.closest('#cdv2-note-clear')) {
        const ta = document.getElementById('mini-cart__notes');
        if (ta) {
          ta.value = '';
          ta.dispatchEvent(new Event('input'));  // triggers char counter update
        }
        return;
      }

      const trig = e.target.closest('.cdv2-price-trigger');
      if (trig) {
        const wrap = trig.closest('.cdv2-price');
        if (wrap) {
          const now = wrap.dataset.open === 'true';
          wrap.dataset.open = String(!now);
          const toggle = trig.querySelector('.cdv2-price-toggle');
          if (toggle) toggle.textContent = now ? 'Show Details' : 'Hide Details';
        }
      }

      const chatLink = e.target.closest('.cdv2-header-readonly-hint a[href="#live-chat"]');
      if (chatLink) {
        e.preventDefault();
        if (window.LiveChatWidget) {
          LiveChatWidget.on('ready', function() {
            LiveChatWidget.call('maximize', { messageDraft: 'Do you offer curtains in other headers?' });
          });
        }
      }
    });

    document.addEventListener('input', (e) => {
      const ta = e.target.closest('#mini-cart__notes');
      if (!ta) return;
      const max = parseInt(ta.getAttribute('maxlength') || '500', 10);
      const counter = document.querySelector('.cdv2-note-count');
      if (counter) counter.textContent = `${ta.value.length} / ${max}`;
    });

    document.addEventListener('cart:refreshed', () => {
      setupCartDrawerDetailToggles();
      const badge = $('#cdv2-badge');
      if (badge) {
        badge.classList.remove('is-bouncing');
        void badge.offsetWidth;
        badge.classList.add('is-bouncing');
      }
      // Re-expand last edited item if cart DOM was replaced after editor closed
      const key = window.cartEditorV2?._pendingExpandKey;
      if (key) {
        const tryExpand = () => {
          const target = findCdv2ItemByKey(key);
          if (target) expandCdv2ItemDetails(target);
        };
        tryExpand();
        setTimeout(tryExpand, 150);
      }
    });
  }

  /* ── Cart Editor Class ───────────────────────────────────── */
  class CartEditor {
    constructor() {
      this.panel = $('#cdv2-editor');
      this.backBtn = $('#cdv2-editor-back');
      this.closeBtn = $('#cdv2-editor-close');
      this.scroll = $('#cdv2-editor-scroll');
      this.footer = $('#cdv2-editor-footer');
      this.saveBtn = $('#cdv2-editor-save');
      this.priceEl = $('#cdv2-editor-price');
      this.priceValEl = $('#cdv2-editor-price-val');
      this.loadingEl = $('#cdv2-editor-loading');
      this.lockEl = $('#cdv2-cart-lock');

      this.activeData = null;   // current item's editor JSON data
      this.pendingChanges = {}; // { variantId, qty, properties, ... }
      this.isDirty = false;
      this._dimensionValid = true;
      this._requiredOptionsValid = true;
      this._subRequiredGroups = [];  // groups still missing their sub-option pick
      this._priceTimer = null;
      this._fieldsAbort = null;

      if (!this.panel) return;
      this._bind();
    }

    _bind() {
      this.backBtn?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.saveBtn?.addEventListener('click', () => this.save());
    }

  /* ── Open editor for an item ─────────────────────────── */
  open(itemEl) {
    const dataScript = itemEl.querySelector('script.cdv2-editor-data');
    if (!dataScript) { console.warn('[CartEditor] No editor data for this item.'); return; }

    let data;
    try { data = JSON.parse(dataScript.textContent); }
    catch (err) { console.error('[CartEditor] Failed to parse editor data', err); return; }

    this.activeData = data;
    // Normalize properties: handle array-format, mixed-format, and number strings
    const rawProps = JSON.parse(JSON.stringify(data.properties || {}));
    const props = this._parseProperties(rawProps);
    // Normalize HTML entities that may have been saved as literal text (e.g. 44&quot;)
    for (const k of Object.keys(props)) {
      props[k] = decodeEntitiesBasic(props[k]);
    }
    normalizeDimensionPropsOnOpen(props, data);

    this.pendingChanges = {
      variantId: data.variant_id,
      qty: data.qty,
      properties: props,
    };
    this.isDirty = false;
    // Per-item, like isDirty: the mounting-type constraints below can turn this off,
    // and only a click on a mounting_type card turns it back on. Without this reset it
    // latches for the whole page session and disables Save on every other cart line —
    // _dimensionValid does not need it because _applyDimensionValidationUI recomputes it.
    this._requiredOptionsValid = true;
    this._subRequiredGroups = [];
    this._updateSaveState();

    // Reset per-session caches so a fresh price is calculated for each item
    this._exchangeRate = null;
    this._variantCache = null;
    this._priceHelpers = null;

    // Render the form
    this._renderEditor(data);

    // Line total (immediate first load; later changes are debounced)
    this._updatePriceDisplay(true);

    this.panel.classList.add('is-open');
    document.body.classList.add('cdv2-editor-open');

    // Full-screen lock: cart not operable while editing
    if (this.lockEl) this.lockEl.removeAttribute('hidden');

    setTimeout(() => {
      const first = this.panel.querySelector('button:not([disabled]), input:not([disabled])');
      first?.focus();
    }, 320);

    document.dispatchEvent(new CustomEvent('cart-editor:open', { detail: { key: data.key } }));
  }

  /* ── Close editor ───────────────────────────────────────── */
  close() {
    if (this.isDirty && !window.confirm('Discard changes?')) return;
    clearTimeout(this._priceTimer);
    this._priceTimer = null;
    document.body.classList.remove('cdv2-editor-open');
    const errEl = document.getElementById('cdv2-editor-price-error');
    if (errEl) {
      errEl.hidden = true;
      errEl.textContent = '';
    }
    this.panel.classList.remove('is-open');
    if (this.lockEl) this.lockEl.setAttribute('hidden', '');
    setTimeout(() => {
      this.activeData = null;
      this.pendingChanges = {};
    }, 340);
    // Clear pending expand key after enough time for any secondary refresh to complete
    setTimeout(() => { this._pendingExpandKey = null; }, 3000);
    document.dispatchEvent(new CustomEvent('cart-editor:close'));
  }

    /* ── Save (update cart item) ─────────────────────────── */
    async save() {
      if (!this.activeData) return;
      const { key } = this.activeData;
      const { variantId, qty, properties } = this.pendingChanges;

      this._applyDimensionValidationUI();
      const dimCheck = validateDimensionsForEditor(this.activeData, properties);
      if (!dimCheck.valid) {
        showToast('Please fix the size fields before saving.');
        return;
      }

      // Compound sub-options (Trim Type → material, Lift Control → sub) must not be
      // left on the parent-only value: it is unpriceable and would check out without
      // its surcharge. See _findIncompleteSubGroups. Also catches lines that were
      // already saved in that state before this guard existed.
      const incompleteSubs = this._findIncompleteSubGroups();
      if (incompleteSubs.length) {
        this._applySubSelectionUI();
        const first = incompleteSubs[0];
        showToast(`Please choose a ${this._subLabelFor(first)} before saving.`);
        const target = this.panel.querySelector(`[data-sub-error="${first.type}"]`)
          || this.panel.querySelector(`[data-group="${first.type}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Motorized shades require Room Name (mirrors shades PDP addToCart validation)
      if (this.activeData.template?.includes('shades') && properties['_lift_control'] === 'motorized') {
        const roomVal = (properties['Room Name'] || '').trim();
        const roomErrEl = this.panel.querySelector('#cdv2-room-name-error');
        if (!roomVal) {
          if (roomErrEl) {
            roomErrEl.textContent = 'Room name is required for motorized shades.';
            roomErrEl.style.display = '';
          }
          const roomInput = this.panel.querySelector('#cdv2-field-room_name');
          roomInput?.focus();
          return;
        } else if (roomErrEl) {
          roomErrEl.style.display = 'none';
        }
      }

      // custom-track-bend requires Bend Note
      if (this.activeData.template === 'custom-track-bend') {
        const bendVal = (properties['Bend Note'] || '').trim();
        const bendErrEl = this.panel.querySelector('#cdv2-bend-note-error');
        if (!bendVal) {
          if (bendErrEl) { bendErrEl.textContent = 'Bend note is required.'; bendErrEl.style.display = ''; }
          this.panel.querySelector('#cdv2-field-bend_note')?.focus();
          return;
        } else if (bendErrEl) {
          bendErrEl.style.display = 'none';
        }
      }

      this._setLoading(true);

      try {
        // Build clean properties — exclude system / auto-generated props
        // NOTE: do NOT exclude Weight/Pleat/Hook; they may exist and change with edits.
        const EXCLUDE = ['_cart_edit_token', '_cart_edit_source'];

        // Rex: always sync _track_type = _rod_type before save (mirrors PDP v6 buildProperties line 977)
        // This runs unconditionally so saves that don't touch Rod Type still keep them in sync.
        if (properties._rod_type) properties._track_type = properties._rod_type;

        // Best-effort: update derived props before saving
        this._applyDerivedPropsBeforeSave();

        const cleanProps = {};
        for (const [k, v] of Object.entries(properties)) {
          if (EXCLUDE.includes(k)) continue;
          // Skip blank strings; keep numbers (including 0) and booleans
          if (v === null || v === undefined) continue;
          if (typeof v === 'string' && v.trim() === '') continue;
          cleanProps[k] = decodeEntitiesBasic(v);
        }

        const sameVariant = variantId === this.activeData.variant_id;
        let editedLineKey = key;

        if (sameVariant) {
          const cart = await this._cartChange(key, qty, cleanProps);
          const line = Number(this.activeData.line) || 1;
          editedLineKey = cart.items[line - 1]?.key || editedLineKey;
        } else {
          window.__cartEditorSaving = true;
          try {
            await this._cartChange(key, 0);
            const addPayload = { id: variantId, quantity: qty };
            if (Object.keys(cleanProps).length > 0) addPayload.properties = cleanProps;
            const added = await this._cartAdd(addPayload);
            editedLineKey = added.key || editedLineKey;
          } finally {
            window.__cartEditorSaving = false;
          }
        }

        // Refresh the cart section
        await this._refreshCart();

        // Store key so cart:refreshed listener can re-expand if theme re-refreshes after close
        this._pendingExpandKey = editedLineKey;
        this._expandEditedItemDetails(editedLineKey);

        this._setLoading(false);
        this.isDirty = false;
        showToast('Cart updated successfully');
        this.close();

      } catch (err) {
        console.error('[CartEditor] Save error:', err);
        if (err && err.code === 429) showToast('Too many requests, please wait a moment.');
        else showToast('Could not update cart. Please try again.');
        this._setLoading(false);
      }
    }

    /* ── Cart API calls ─────────────────────────────────── */
    async _cartChange(key, quantity, properties = null) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity, ...(properties ? { properties } : {}) }),
      });
      if (res.status === 429) { const e = new Error('rate_limited'); e.code = 429; throw e; }
      if (!res.ok) throw new Error('Cart change failed');
      return res.json();
    }

    async _cartAdd(payload) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) { const e = new Error('rate_limited'); e.code = 429; throw e; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Cart add failed');
      }
      return res.json();
    }

    async _refreshCart() {
      // First try theme CartDrawer.refresh() via event (keeps behavior consistent)
      const refreshed = await new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (done) return;
          done = true;
          document.removeEventListener('cart:refreshed', onRefreshed);
          resolve(ok);
        };
        const onRefreshed = () => finish(true);
        document.addEventListener('cart:refreshed', onRefreshed, { once: true });
        setTimeout(() => finish(false), 2000);
        if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('cart:refresh');
        else document.dispatchEvent(new CustomEvent('cart:refresh'));
      });

      if (refreshed) return;

      // Fallback: pull sections and replace DOM.
      const sections = ['cart-drawer', 'cart-bubble'];
      if (window.location && window.location.pathname === '/cart') sections.push('main-cart');
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('Cart refresh failed');
      const parsed = await res.json();

      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && parsed['cart-drawer']) {
        const doc = new DOMParser().parseFromString(parsed['cart-drawer'], 'text/html');
        const newDrawer = doc.querySelector('.cart-drawer') || doc.querySelector('#Cart-Drawer');
        if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
      }

      const bubble = document.getElementById('cart-drawer-toggle');
      if (bubble && parsed['cart-bubble']) {
        const doc = new DOMParser().parseFromString(parsed['cart-bubble'], 'text/html');
        const newCount = doc.querySelector('.thb-item-count');
        const oldCount = bubble.querySelector('.thb-item-count');
        if (newCount && oldCount) oldCount.innerHTML = newCount.innerHTML;
      }

      const cart = document.getElementById('Cart');
      if (cart && parsed['main-cart']) {
        const doc = new DOMParser().parseFromString(parsed['main-cart'], 'text/html');
        const newForm = doc.querySelector('.cart-form') || doc.querySelector('.thb-cart-form');
        const oldForm = cart.querySelector('.cart-form') || cart.querySelector('.thb-cart-form');
        if (newForm && oldForm) oldForm.innerHTML = newForm.innerHTML;
      }

      if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('cart:refreshed');
      else document.dispatchEvent(new CustomEvent('cart:refreshed'));

      // Seel Worry-Free widget listens for this event to re-inject after cart updates
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));

      setupCartDrawerDetailToggles();
    }

    /** After save, expand the edited line's "Show more" panel (match by unique line item key). */
    _expandEditedItemDetails(lineKey) {
      const tryExpand = () => {
        const target = findCdv2ItemByKey(lineKey);
        if (target) expandCdv2ItemDetails(target);
      };
      // Always retry via rAF + timeout: theme may replace DOM asynchronously after cart:refreshed fires
      tryExpand();
      requestAnimationFrame(() => {
        tryExpand();
        setTimeout(tryExpand, 200);
      });
    }

    /* ── Note save ───────────────────────────────────────── */
    async _saveNote() {
      const ta = $('#mini-cart__notes');
      if (!ta) return;
      const saveBtn = $('.cdv2-note-save');
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: ta.value }),
        });
        showToast('Saved successfully');
        const noteWrap = ta.closest('.cdv2-note');
        if (noteWrap) {
          noteWrap.dataset.open = 'false';
          const tog = document.getElementById('order-note-toggle');
          if (tog) tog.setAttribute('aria-expanded', 'false');
        }
      } catch (err) {
        showToast('Could not save note');
      } finally {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
      }
    }

    /* ── Loading state ───────────────────────────────────── */
    _setLoading(on) {
      if (this.saveBtn) this.saveBtn.disabled = on;
      if (this.loadingEl) this.loadingEl.hidden = !on;
      if (on && this.scroll) {
        const layoutEl = this.scroll.querySelector('.cdv2-editor-layout');
        if (layoutEl) layoutEl.style.opacity = '0.4';
      } else if (this.scroll) {
        const layoutEl = this.scroll.querySelector('.cdv2-editor-layout');
        if (layoutEl) layoutEl.style.opacity = '';
      }
    }

    _updateSaveState() {
      if (this.saveBtn) {
        this.saveBtn.disabled = !this.isDirty
          || !this._dimensionValid
          || !this._requiredOptionsValid
          || (this._subRequiredGroups && this._subRequiredGroups.length > 0);
      }
    }

    _applyDimensionValidationUI() {
      const form = this.scroll;
      if (!form || !this.activeData) return;
      const result = validateDimensionsForEditor(this.activeData, this.pendingChanges.properties);
      this._dimensionValid = result.valid;

      // Rex: highlight xyz fields on width error
      if (isRexTrack(this.activeData)) {
        $$('.cdv2-dim-xyz-field', form).forEach(f => f.classList.toggle('has-error', !!result.width));
      }

      ['width', 'length'].forEach((dim) => {
        const msg = dim === 'width' ? result.width : result.length;
        const errEl = form.querySelector(`[data-dim-error="${dim}"]`);
        const input = form.querySelector(`[data-dim="${dim}"]`);
        if (errEl) {
          errEl.textContent = msg;
          errEl.hidden = !msg;
        }
        if (input) input.classList.toggle('has-error', !!msg);
      });

      this._updateSaveState();
    }


    _compoundSubGroups() {
      const data = this.activeData;
      if (!data) return [];
      const groups = [];
      if (data.lift_controls && data.lift_controls.length) {
        groups.push({
          type: 'controls',
          label: 'Lift Control',
          propKey: '_lift_control',
          priceKey: 'lift_control',
          items: data.lift_controls,
        });
      }
      for (const grp of (data.extra_options || [])) {
        if (grp.option_id === 'lift_control') continue;
        if (!grp.items || !grp.items.length) continue;
        groups.push({
          type: `extra-${grp.option_id}`,
          label: grp.label,
          propKey: grp.prop_key,
          // custom-curtain family calls the group 'trim' but prices it as 'trim_type'
          priceKey: grp.option_id === 'trim' ? 'trim_type' : grp.option_id,
          items: grp.items,
        });
      }
      return groups;
    }

    // Can this value actually be priced? A parent-only value only costs the customer
    // money when it is NOT priceable — that is exactly when the surcharge silently
    // becomes 0. Options the template does not price at all (roller_type) and PDPs
    // that legitimately treat the parent as the final selection are left alone.
    _isPriceableOptionValue(priceKey, value) {
      const tpl = this.activeData && this.activeData.price_template;
      const co = tpl && tpl.custom_option;
      if (!co || !co[priceKey]) return true;
      const calc = window.PriceCalc && window.PriceCalc.resolveCustomOptionPrice;
      if (typeof calc !== 'function') return true;
      try {
        calc(tpl, priceKey, value, { width: 0, length: 0, paramHeader: 0 });
        return true;
      } catch (e) {
        return false;
      }
    }

    // Groups whose stored value is still an unpriceable parent param of an item
    // that has sub-options.
    _findIncompleteSubGroups() {
      const props = this.pendingChanges.properties || {};
      const incomplete = [];
      for (const grp of this._compoundSubGroups()) {
        const val = props[grp.propKey];
        if (!val) continue;  // empty value — governed by each group's own rules
        const parent = grp.items.find(it => it.param === val);
        if (!parent) continue;  // compound (sub) value → complete
        if (!parent.has_sub || !parent.subs || !parent.subs.length) continue;
        if (this._isPriceableOptionValue(grp.priceKey, val)) continue;
        incomplete.push({ ...grp, parent });
      }
      return incomplete;
    }

    // Label for the sub-option step — kept in sync with the sub-panel heading
    // rendered by _renderCardGroup.
    _subLabelFor(grp) {
      if (grp.parent && grp.parent.subcategory_title) return grp.parent.subcategory_title;
      if (grp.propKey === '_trim_type') return 'Trim Material';
      return 'style';
    }

    // Show/hide the per-group "pick a sub-option" message and gate the save button.
    _applySubSelectionUI() {
      this._subRequiredGroups = this._findIncompleteSubGroups();
      if (this.panel) {
        const pending = new Map(this._subRequiredGroups.map(g => [g.type, g]));
        this.panel.querySelectorAll('[data-sub-error]').forEach((el) => {
          const grp = pending.get(el.dataset.subError);
          if (grp) {
            el.textContent = `Please choose a ${this._subLabelFor(grp)} for “${grp.parent.title}”.`;
            el.hidden = false;
          } else {
            el.textContent = '';
            el.hidden = true;
          }
        });
      }
      this._updateSaveState();
    }

    _markDirty() {
      this.isDirty = true;
      // Before validation/price/summary below, so a width edit that invalidates
      // Inside Mount is reflected in all three.
      this._applyRomanInsideMountConstraint();
      this._applyDimensionValidationUI();
      // Keep derived fields (Weight / Pleat / Hook) in sync for summary + save.
      try { this._applyDerivedPropsBeforeSave(); } catch (e) {}
      this._updatePriceDisplay();
      this._updateSelectedSummary();
      this._updateDimensionLabel();
      // Some fields (e.g. color summary) rely on DOM being ready; force a microtask refresh.
      queueMicrotask(() => this._updateSelectedSummary());
      // Custom curtain: lining/design/trim prices depend on dimensions — re-calculate on every change.
      // Shades: lining_option/trim_type also dimension-dependent — re-fetch on every change.
      if (this.activeData) {
        const tmpl = this.activeData.template || '';
        const props = this.pendingChanges.properties;
        const isCustom = ['custom','custom-door','custom-cafe'].includes(tmpl);
        const isShades = tmpl.includes('shades');
        if (isCustom) {
          this._loadCustomCurtainOptionPrices(this.activeData, props);
        }
        if (tmpl === 'custom-valance') {
          this._loadValanceLiningPrices(this.activeData, props);
        }
        if (isShades) {
          this._loadExtraOptionPrices(this.activeData, props);
        }
      }
    }

    _updatePriceDisplay(immediate = false) {
      if (!this.activeData || !this.priceEl || !this.priceValEl) return;
      this.priceEl.removeAttribute('hidden');
      const errEl = document.getElementById('cdv2-editor-price-error');
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = '';
      }
      this.priceValEl.textContent = '…';
      clearTimeout(this._priceTimer);
      if (immediate) {
        this._fetchItemPrice();
        return;
      }
      this._priceTimer = setTimeout(() => this._fetchItemPrice(), 380);
    }

    /* ══════════════════════════════════════════════════════════════════════
       PRICE CALCULATION — dispatch by template
       Each template family has its own method below.
       To change one family's pricing, edit only its method.
    ══════════════════════════════════════════════════════════════════════ */

    async _fetchItemPrice() {
      if (!this.activeData) return;
      const tmpl = this.activeData.template || '';

      // helper refs shared across methods
      this._priceHelpers = this._priceHelpers || {
        noteEl:  document.getElementById('cdv2-editor-price-note'),
        errEl:   document.getElementById('cdv2-editor-price-error'),
        showNote: (show) => {
          const el = document.getElementById('cdv2-editor-price-note');
          if (el) el.hidden = !show;
        },
        hideError: () => {
          const el = document.getElementById('cdv2-editor-price-error');
          if (el) { el.hidden = true; el.textContent = ''; }
        },
        showError: (msg) => {
          const el = document.getElementById('cdv2-editor-price-error');
          if (el) { el.textContent = msg || 'Could not load price.'; el.hidden = false; }
          showToast('Pricing is temporarily unavailable. Check your connection or try again.');
        },
      };

      this._priceHelpers.hideError();

      try {
        // ── custom / custom-door / custom-cafe ──
        if (tmpl === 'custom' || tmpl === 'custom-door' || tmpl === 'custom-cafe') {
          return await this._priceCustomCurtain();
        }
        // ── custom-valance / custom-swag ──
        if (tmpl === 'custom-valance' || tmpl === 'custom-swag') {
          return await this._priceCurtainApi();
        }
        // ── shades ──
        if (tmpl.includes('shades')) {
          return await this._priceShadesApi();
        }
        // ── custom-track family ──
        if (tmpl.includes('custom-track') || tmpl === 'track-backup') {
          return await this._priceTrackApi();
        }
        // ── static price (standard, pillow-cover, glamour …) ──
        await this._priceStaticVariant();
      } catch (err) {
        console.warn('[CartEditor] Price error:', err);
        this._priceHelpers.showError('Network error while loading price.');
        if (this.priceValEl) this.priceValEl.textContent = '—';
      }
    }

    /* ── custom / custom-door / custom-cafe ──────────
       Local formula mirrors product-custom-variants.liquid getFinalPrice().
       Uses price_template + curtain_parameters embedded in activeData.
    ──────────────────────────────────────────────────────────────────── */
    async _priceCustomCurtain() {
      const data  = this.activeData;
      const props = this.pendingChanges.properties;
      const qty   = this.pendingChanges.qty;
      const { showNote, showError } = this._priceHelpers;

      const priceTemplate      = data.price_template;
      const curtainParameters  = data.curtain_parameters;

      if (!priceTemplate || !curtainParameters) {
        showNote(true);
        if (this.priceValEl) this.priceValEl.textContent = '—';
        return;
      }

      // _width/_length always store inches (PDP buildProperties converts cm→in before cart add)
      const rawW = this._extractNum(props['_width'] || '');
      const rawL = this._extractNum(props['_length'] || '');
      if (!rawW || !rawL) {
        if (this.priceValEl) this.priceValEl.textContent = '—';
        showNote(false);
        return;
      }

      const w = parseFloat(rawW);
      const l = parseFloat(rawL);

      const header  = props['_header'] || '';
      const lining  = props['_lining'] || '';
      const design  = props['_design'] || '';
      const trimRaw = props['_trim_type'] || '';

      const paramHeader  = header ? parseFloat(priceTemplate.custom_curtain.header?.[header] || 0) : 0;
      const per          = parseFloat(priceTemplate.per);

      const curtainSize  = _curtainCalcSize(w, l, curtainParameters, priceTemplate);

      let liningPrice = 0;
      if (lining && priceTemplate.custom_curtain.lining?.[lining] != null) {
        const pL = parseFloat(priceTemplate.custom_curtain.lining[lining]);
        liningPrice = _curtainCalcLining(w, l, paramHeader, pL, priceTemplate);
      }

      let designPrice = 0;
      if (design && priceTemplate.custom_curtain.design?.[design] != null) {
        const pD = parseFloat(priceTemplate.custom_curtain.design[design]);
        designPrice = _curtainCalcDesign(w, l, design, paramHeader, pD);
      }

      let trimPrice = 0;
      if (trimRaw && trimRaw !== 'none') {
        const parts = trimRaw.split('-', 2);
        if (parts.length === 2 && parts[0] && parts[1]) {
          const pT = parseFloat(priceTemplate.custom_option?.trim_type?.[parts[1]] || 0);
          trimPrice = _curtainCalcTrim(w, l, paramHeader, parts[0], pT, per);
        }
      }

      // Use shared _getUsdPrices() to align with _priceCurtainApi/_priceShadesApi/_priceTrackApi:
      // backend exchange-rate gives Admin-API USD (correct for DE market); fall back to
      // /products/X.js when unavailable. discountRate is a ratio so same-currency is fine.
      const exchangeRate = await this._getExchangeRate();
      const { listPrice, compareAtPrice } = await this._getUsdPrices();
      const discountRate = (compareAtPrice && listPrice) ? Number((listPrice / compareAtPrice).toFixed(4)) : 1.0;

      const localPrice   = _curtainCalcFinalPrice(curtainSize, paramHeader, liningPrice, trimPrice, designPrice, exchangeRate, discountRate);
      const totalCents   = Math.round(localPrice * 100) * qty;

      if (this.priceValEl) this.priceValEl.textContent = money(totalCents);
      showNote(false);
    }

    /* ── custom-valance / custom-swag  (LOCAL curtain calc) ─────────────
       mirrors product-variants-v11/v12.liquid getFinalPrice()
    ──────────────────────────────────────────────────────────────────── */
    async _priceCurtainApi() {
      const data  = this.activeData;
      const props = this.pendingChanges.properties;
      const qty   = this.pendingChanges.qty;
      const { showNote, showError } = this._priceHelpers;

      // _width/_length always store inches
      const rawW  = this._extractNum(props['_width'] || '');
      const rawL  = this._extractNum(props['_length'] || '');
      if (!rawW || !rawL) { if (this.priceValEl) this.priceValEl.textContent = '—'; return; }

      const priceTemplate     = data.price_template;
      const curtainParameters = data.curtain_parameters;
      if (!priceTemplate || !curtainParameters) {
        showError('Pricing template not available.');
        if (this.priceValEl) this.priceValEl.textContent = '—';
        return;
      }

      const w = parseFloat(rawW);
      const l = parseFloat(rawL);
      const header = props['_header'] || '';
      const lining = props['_lining'] || '';
      const design = props['_design'] || '';

      const paramHeader = header ? (parseFloat(priceTemplate.custom_curtain.header?.[header]) || 0) : 0;
      const paramLining = lining ? (parseFloat(priceTemplate.custom_curtain.lining?.[lining]) || 0) : 0;
      const paramDesign = design ? (parseFloat(priceTemplate.custom_curtain.design?.[design]) || 0) : 0;
      const per = parseFloat(priceTemplate.per);

      const curtainSize = window.PriceCalc.calcSize(w, l, curtainParameters, priceTemplate);

      // Lining price (USD, no exchangeRate) — mirrors run.rs CustomCurtain::get_lining,
      // which rounds to a whole dollar.
      let multiplier = 1;
      const baseValue = (paramLining * w) / 35;
      if (l > 104) multiplier = Math.ceil((l * 2) / 104) / 2;
      const liningPrice = Math.round(baseValue * multiplier * (1 + paramHeader / 100) * per);

      const trimPrice = 0; // v11/v12 don't pass trim_type
      const designPrice = window.PriceCalc.calcDesign(w, l, design, paramHeader, paramDesign);

      const localCurrency = await this._getLocalCurrency();
      const { listPrice, compareAtPrice } = await this._getUsdPrices();
      const discountRate = (compareAtPrice && listPrice) ? Number((listPrice / compareAtPrice).toFixed(4)) : 1.0;

      const result = window.PriceCalc.calcPrice(curtainSize, paramHeader, liningPrice, trimPrice, designPrice, localCurrency, discountRate);

      if (this.priceValEl) this.priceValEl.textContent = money(Math.round(parseFloat(result.localPrice) * 100) * qty);
      showNote(false);
    }

    /* ── shades  (LOCAL shades calc) ───────────────────────────────────
       mirrors product-variants-v5.liquid getFinalPrice()
    ──────────────────────────────────────────────────────────────────── */
    async _priceShadesApi() {
      const data  = this.activeData;
      const props = this.pendingChanges.properties;
      const qty   = this.pendingChanges.qty;
      const { showNote, showError } = this._priceHelpers;

      // _width/_length always store inches for shades (PDP buildProperties converts cm→in)
      const rawW = this._extractNum(props['_width'] || '');
      const rawL = this._extractNum(props['_length'] || '');
      if (!rawW || !rawL) { if (this.priceValEl) this.priceValEl.textContent = '—'; return; }

      const priceTemplate     = data.price_template;
      const curtainParameters = data.curtain_parameters;
      if (!priceTemplate || !curtainParameters || !priceTemplate.shades) {
        showError('Pricing template not available.');
        if (this.priceValEl) this.priceValEl.textContent = '—';
        return;
      }

      const w = parseFloat(rawW);
      const l = parseFloat(rawL);
      const per = parseFloat(priceTemplate.per);

      const sizePrice = window.PriceCalc.calcShadesSizePrice(curtainParameters, priceTemplate.shades, per, w, l);

      // priceOptions: iterate lift_control + extra_options (mirrors PDP v5 additionalOptions)
      let priceOptions = 0;
      const ctx = { width: w, length: l, paramHeader: 0 };

      const addOption = (optKey, val) => {
        if (!val) return;
        try {
          priceOptions += window.PriceCalc.resolveCustomOptionPrice(priceTemplate, optKey, val, ctx);
        } catch (e) {
          console.warn('[CartEditor] shades option price error', optKey, val, e);
        }
      };

      if (props['_lift_control']) addOption('lift_control', props['_lift_control']);
      for (const grp of (data.extra_options || [])) {
        if (grp.option_id === 'lift_control') continue;
        const val = props[grp.prop_key];
        if (!val) continue;
        // Skip parent-only param when group has sub items requiring compound value
        const hasSubItems = grp.items && grp.items.some(it => it.has_sub && it.subs && it.subs.length);
        if (hasSubItems && grp.items.some(it => it.param === val)) continue;
        addOption(grp.option_id, val);
      }

      const localCurrency = await this._getLocalCurrency();
      const { listPrice, compareAtPrice } = await this._getUsdPrices();
      const discountRate = compareAtPrice ? (listPrice / compareAtPrice) : 1;

      const result = window.PriceCalc.calcShadesPrice(Number(sizePrice), priceOptions, localCurrency, discountRate);

      if (this.priceValEl) this.priceValEl.textContent = money(Math.round(parseFloat(result.localPrice) * 100) * qty);
      showNote(false);
    }

    /* ── custom-track  (LOCAL track calc) ──────────────────────────────
       mirrors product-variants-v3.liquid getFinalPrice()
    ──────────────────────────────────────────────────────────────────── */
    async _priceTrackApi() {
      const data  = this.activeData;
      const props = this.pendingChanges.properties;
      const qty   = this.pendingChanges.qty;
      const { showNote, showError } = this._priceHelpers;

      // _track_width/_width always store inches
      const rawW = this._extractNum(props['_track_width'] || props['_width'] || '');
      if (!rawW) { if (this.priceValEl) this.priceValEl.textContent = '—'; return; }

      const priceTemplate = data.price_template;
      if (!priceTemplate || !priceTemplate.custom_option) {
        showError('Pricing template not available.');
        if (this.priceValEl) this.priceValEl.textContent = '—';
        return;
      }

      const width = parseFloat(rawW);
      const per = parseFloat(priceTemplate.per);
      const version = data.track_calculate_type || 'v1';

      const localCurrency = await this._getLocalCurrency();
      const { listPrice, compareAtPrice } = await this._getUsdPrices();
      if (!listPrice) {
        showError('Variant base price unavailable.');
        if (this.priceValEl) this.priceValEl.textContent = '—';
        return;
      }

      const sizePrice = window.PriceCalc.calcTrackSizePrice(version, listPrice, width, per);

      let priceOptions = 0;
      let trackType = 0;

      // width_type: backend pushes into additionalOptions when present
      const optKeys = [];
      if (props._width_type) optKeys.push('width_type');
      for (const optId of (data.track_additional_options || [])) optKeys.push(optId);

      optKeys.forEach((optKey) => {
        const paramOption = optKey === 'width_type'
          ? props._width_type
          : props[`_${optKey}`];
        if (!paramOption) return;
        if (!priceTemplate.custom_option[optKey]) return;
        if (optKey !== 'track_type' && optKey !== 'rod_type') {
          const p = parseFloat(priceTemplate.custom_option[optKey][paramOption]) || 0;
          priceOptions += p;
        } else {
          trackType = priceTemplate.custom_option[optKey][paramOption] || 0;
        }
      });

      const discountRate = compareAtPrice ? (listPrice / compareAtPrice) : 1;
      // Mirrors backend: priceOptions = calculateType == 'v3' ? 0 : priceOptions
      if (version === 'v3') priceOptions = 0;

      const result = window.PriceCalc.calcTrackPrice(Number(sizePrice), priceOptions, trackType, localCurrency, discountRate);

      if (this.priceValEl) this.priceValEl.textContent = money(Math.round(parseFloat(result.localPrice) * 100) * qty);
      showNote(false);
    }

    /* ── static variant price (standard, pillow-cover, glamour …) ────── */
    async _priceStaticVariant() {
      const data      = this.activeData;
      const variantId = this.pendingChanges.variantId;
      const qty       = this.pendingChanges.qty;
      const { showNote, showError } = this._priceHelpers;

      if (variantId !== data.variant_id) {
        const prodRes = await fetch(`/products/${encodeURIComponent(data.product_handle)}.js`);
        if (!prodRes.ok) { showError('Could not load product prices.'); if (this.priceValEl) this.priceValEl.textContent = '—'; return; }
        const product = await prodRes.json();
        const v = product.variants.find(pv => pv.id === variantId);
        if (this.priceValEl) this.priceValEl.textContent = money((v ? v.price : data.price) * qty);
      } else {
        if (this.priceValEl) this.priceValEl.textContent = money(data.line_price);
      }
      showNote(false);
    }

    /* ── Local currency (exchangeRate + listPrice + compareAtPrice), cached ──
       Delegates to window.PriceCalc.fetchExchangeRate which short-circuits to
       USD for US/empty countryCode, and returns the extended object including
       listPrice/compareAtPrice from Admin API for other markets. */
    async _getLocalCurrency() {
      if (this._localCurrency) return this._localCurrency;
      const priceData   = window.__cartPriceData__ || {};
      const countryCode = priceData.countryCode || 'US';
      const variantId   = this.pendingChanges.variantId || (this.activeData && this.activeData.variant_id);
      if (window.PriceCalc && typeof window.PriceCalc.fetchExchangeRate === 'function') {
        this._localCurrency = await window.PriceCalc.fetchExchangeRate(variantId, countryCode);
      } else {
        this._localCurrency = { exchangeRate: 1.0, currencyCode: 'USD' };
      }
      return this._localCurrency;
    }

    /* ── Exchange rate (cached per editor session) ──────────────────── */
    async _getExchangeRate() {
      if (this._exchangeRate != null) return this._exchangeRate;
      const lc = await this._getLocalCurrency();
      this._exchangeRate = parseFloat(lc.exchangeRate) || 1.0;
      return this._exchangeRate;
    }

    /* ── Resolve USD listPrice & compareAtPrice ──────────────────────────
       Strategy mirrors PDP files: backend exchange-rate (Admin API USD)
       first; fall back to /products/X.js variant (local-currency cents
       converted to USD via exchangeRate) ONLY when backend gave no value.
       Note: a true compareAtPrice=0 from backend (= variant has no
       compare-at) must be respected — do NOT pollute it from /products/X.js. */
    async _getUsdPrices() {
      const lc = await this._getLocalCurrency();
      const exRate = parseFloat(lc.exchangeRate) || 1;
      let listPrice = (lc.listPrice != null) ? Number(lc.listPrice) : 0;
      // backend explicitly returned 0/null/undefined → respect "no compare-at"
      // backend never sets the field → fall back to /products/X.js
      const hasBackendCompareAt = Object.prototype.hasOwnProperty.call(lc, 'compareAtPrice') && lc.compareAtPrice !== null;
      let compareAtPrice = hasBackendCompareAt ? (Number(lc.compareAtPrice) || 0) : 0;

      // Only fall back to Storefront when backend gave nothing usable.
      const needListFallback    = !listPrice;
      const needCompareFallback = !hasBackendCompareAt;
      if (needListFallback || needCompareFallback) {
        const variant = await this._getVariant();
        if (variant) {
          if (needListFallback && variant.price != null) {
            listPrice = (parseFloat(variant.price) / 100) / exRate;
          }
          if (needCompareFallback && variant.compare_at_price) {
            compareAtPrice = (parseFloat(variant.compare_at_price) / 100) / exRate;
          }
        }
      }
      return { listPrice, compareAtPrice };
    }

    /* ── Current variant data (lazy fetch, cached) ──────────────────── */
    async _getVariant() {
      const variantId = this.pendingChanges.variantId;
      if (!this._variantCache) this._variantCache = {};
      if (this._variantCache[variantId]) return this._variantCache[variantId];
      try {
        const res = await fetch(`/products/${encodeURIComponent(this.activeData.product_handle)}.js`);
        if (res.ok) {
          const product = await res.json();
          product.variants.forEach(v => { this._variantCache[v.id] = v; });
          return this._variantCache[variantId] || null;
        }
      } catch {}
      return null;
    }

    /* ── Render the editor layout ──────────────────────────── */
    _renderEditor(data) {
      const caps = resolveTemplateCaps(data.template);
      // Use pendingChanges.properties (already normalized) for pre-filling forms
      const props = this.pendingChanges.properties || data.properties || {};

      const html = `
        <div class="cdv2-editor-layout">

          <!-- Media column -->
          <div class="cdv2-editor-media" id="cdv2-editor-media">
            <div class="cdv2-editor-images">
              <div class="cdv2-editor-thumbs" id="cdv2-editor-thumbs">
                ${data.images.map((img, i) => `
                  <button class="cdv2-editor-thumb ${i === 0 ? 'is-active' : ''}"
                    data-img-src="${img.src || ''}"
                    style="background-image:url('${img.src || ''}');"
                    aria-label="${img.alt || 'Product image ' + (i + 1)}"
                    type="button"
                  ></button>
                `).join('')}
              </div>
              <div class="cdv2-editor-main-image" id="cdv2-editor-main">
                ${data.images[0] ? `<img src="${data.images[0].src}" alt="${data.images[0].alt || data.title}" loading="lazy">` : ''}
              </div>
            </div>
          </div>

          <!-- Form column -->
          <div class="cdv2-editor-form" id="cdv2-editor-form">
            <p class="cdv2-editor-kicker">Customize in cart</p>
            <h2 class="cdv2-editor-title">${this._esc(data.title)}</h2>
            <p class="cdv2-editor-help">Adjust your selections; the line total updates when pricing is available.</p>

            <div class="cdv2-editor-fields" id="cdv2-editor-fields">
              ${this._renderFields(data, caps, props)}
            </div>

            <div class="cdv2-editor-selected" id="cdv2-editor-selected" aria-live="polite"></div>
          </div>

        </div>
      `;

      if (this.scroll) this.scroll.innerHTML = html;

      // Apply initial color image filter, then wire thumbnail switcher
      const initialColor = data.colors?.find(c => c.variant_id === this.pendingChanges.variantId);
      if (initialColor?.label) {
        this._updateMediaPanel(this._getImagesForColor(initialColor.label, data), data.title);
      } else {
        this._bindThumbs();
      }

      // Wire all interactive fields
      this._bindFields(data, caps);

      this._applyDimensionValidationUI();
      this._applyRexMountingConstraint();
      this._applyRomanInsideMountConstraint();
      this._applySubSelectionUI();
      this._updateSaveState();
      this._updateSelectedSummary();

      // Load option prices after render
      this._loadExtraOptionPrices(data, this.pendingChanges.properties);
      this._loadCustomCurtainOptionPrices(data, this.pendingChanges.properties);
      if ((data.template || '') === 'custom-valance') {
        this._loadValanceLiningPrices(data, this.pendingChanges.properties);
      }
    }

    /* ── Per-template field rendering ─────────────────────────────────────────
       Each template branch is self-contained: lists exactly which fields to show
       and in what order, matching the corresponding PDP add-to-cart flow.
       To adjust a template's behaviour, edit only its case block below.
    ── */
    _renderFields(data, caps, props) {
      return this._renderFieldsForTemplate(data.template || '', data, props);
    }

    _renderFieldsForTemplate(tmpl, data, props) {
      const parts = [];
      const add = (html) => { if (html) parts.push(html); };
      const qty = () => this._renderQtyGroup(this.pendingChanges.qty);

      // ── standard / glamour ──────────────────────────────────
      if (!tmpl || tmpl === 'standard' || tmpl === 'glamour') {
        add(this._fColors(data));
        add(qty());
        return parts.join('');
      }

      // ── pillow-cover ────────────────────────────────────────
      // PDP (v4): Color → other variant options (Size etc.) → Fabric text input
      if (tmpl === 'pillow-cover') {
        add(this._fColors(data));
        add(this._fVariantOptions(data));
        add(this._renderFabricField(props));
        add(qty());
        return parts.join('');
      }

      // ── shades (all variants: shades-light, shades-blackout …) ──
      // PDP (v5): Color → Mount Type → Lift Control → Size → extra options(lining_option/roller_type/trim_type)
      if (tmpl.includes('shades')) {
        add(this._fColors(data));
        add(this._fMounts(data, props));
        add(this._fControls(data, props));
        add(this._renderDimensionGroup(props, data, resolveTemplateCaps(tmpl)));
        add(this._fExtraOptions(data, props));
        add(qty());
        return parts.join('');
      }

      // ── custom-valance ──────────────────────────────────────
      // PDP (v11): Color → Header (read-only) → Mount Type → Size → Lining → Design (optional)
      if (tmpl === 'custom-valance') {
        add(this._fColors(data));
        add(this._renderHeaderReadOnly(props, data));
        add(this._fMounts(data, props));
        add(this._renderDimensionGroup(props, data, resolveTemplateCaps(tmpl)));
        add(this._fLinings(data, props));
        add(this._fDesignOptions(data, props));
        add(qty());
        return parts.join('');
      }

      // ── custom-swag ─────────────────────────────────────────
      // PDP (v12): Color → Header (read-only) → Mount Type → Size (+ Room) → Design (optional)
      if (tmpl === 'custom-swag') {
        add(this._fColors(data));
        add(this._renderHeaderReadOnly(props, data));
        add(this._fMounts(data, props));
        add(this._renderDimensionGroup(props, data, resolveTemplateCaps(tmpl)));
        add(this._fDesignOptions(data, props));
        add(qty());
        return parts.join('');
      }

      // ── custom-track family ─────────────────────────────────
      // PDP (v3): Color → Size (track width) → Additional options (finial_style, mounting_type…) → Room [+ Bend Note]
      if (tmpl.includes('custom-track') || tmpl === 'track-backup') {
        add(this._fColors(data));
        add(this._renderDimensionGroup(props, data, resolveTemplateCaps(tmpl)));
        add(this._fExtraOptions(data, props));
        add(this._renderBendNoteField(props, resolveTemplateCaps(tmpl)));
        add(qty());
        return parts.join('');
      }

      // ── custom / custom-door / custom-cafe ──
      // PDP (v1/v2): Color → Header(RO) → Size → Lining → Tieback(non-sheer) → Extra options → Design
      if (tmpl === 'custom' || tmpl === 'custom-door' || tmpl === 'custom-cafe') {
        add(this._fColors(data));
        add(this._renderHeaderReadOnly(props, data));
        add(this._renderDimensionGroup(props, data, resolveTemplateCaps(tmpl)));
        add(this._fLinings(data, props));
        add(this._fExtraOptions(data, props));
        add(this._fDesignOptions(data, props));
        add(this._fTiebacks(data, props));
        add(qty());
        return parts.join('');
      }

      // ── fallback ────────────────────────────────────────────
      add(this._fColors(data));
      add(qty());
      return parts.join('');
    }

    /* ── Field helpers (called by _renderFieldsForTemplate) ── */

    _fColors(data) {
      if (!data.colors || !data.colors.length) return '';
      // Products without a real Color option: no swatches — options rendered via _fVariantOptions instead.
      if (data.has_color_option === false) return '';
      let activeId = this.pendingChanges.variantId;
      // Multi-option products (e.g. pillow-cover with Color + Size): match swatch by color label
      // instead of variant ID, since data.colors stores only the first variant per color.
      if (data.all_variants && data.all_variants.length && data.color_option_index != null) {
        const currentOpts = this._getCurrentVariantOptions(data);
        const currentColorVal = currentOpts[data.color_option_index];
        if (currentColorVal) {
          const match = data.colors.find(c => c.label === currentColorVal);
          if (match) activeId = match.variant_id;
        }
      }
      return this._renderColorGroup(data.colors, activeId);
    }

    _fLinings(data, props) {
      if (!data.linings || data.linings.length === 0) return '';
      const current = props['Lining'] || props['_lining'] || '';
      const sub = props['_lining_sub'] || '';
      // Filter lining swatches by the (header, fabric) matrix — shared source of
      // truth in snippets/lining-header-rules.liquid (mirrors the PDP).
      let linings = data.linings;
      const headerParam = this._getHeaderParam(props, data);
      if (headerParam && window.LiningRules) {
        const fabricType = this._getFabricType(data);
        linings = linings.filter(l =>
          window.LiningRules.isAllowed(headerParam, (l && l.param) || '', data.title, fabricType)
        );
      }
      // Custom curtain + valance: show price spans (all local, no API)
      const showPrices = ['custom','custom-door','custom-cafe','custom-valance'].includes(data.template || '');
      return this._renderCardGroup('Lining', linings, current, 'lining', sub, showPrices);
    }

    // Look up fabric type ('布' / '纱' / …) for the currently selected variant.
    _getFabricType(data) {
      const map = data && data.fabric_type_by_variant;
      if (!map) return '';
      const vid = (this.pendingChanges && this.pendingChanges.variantId) || (data && data.variant_id);
      return map[vid] || map[String(vid)] || '';
    }

    // Resolve the lining's backend param value (e.g. "triple_weave_lining") from
    // cart line props. Prefers `_lining` (already backend); falls back to looking
    // up the lining metaobject by Title for legacy cart lines.
    _getLiningParam(props, data) {
      if (props && props._lining) return props._lining;
      const title = (props && props.Lining) || '';
      if (!title || !data || !data.linings) return '';
      const mo = data.linings.find(l => l && l.title === title);
      return (mo && mo.param) || '';
    }

    // Resolve the header's backend param (e.g. "euro_pleat_triple"). Prefers
    // `_header`; falls back to matching the Header title for legacy cart lines.
    _getHeaderParam(props, data) {
      if (props && props._header) return props._header;
      const title = (props && props.Header) || '';
      if (!title || !data || !data.headers) return '';
      const mo = data.headers.find(h => h && h.title === title);
      return (mo && mo.param) || '';
    }

    _fMounts(data, props) {
      if (!data.mount_types || data.mount_types.length === 0) return '';
      const current = props['Mount Type'] || props['_mount_type'] || props['_mount'] || '';
      const sub = props['_mount_sub'] || '';
      return this._renderCardGroup('Mount Type', data.mount_types, current, 'mount', sub);
    }

    _fControls(data, props) {
      if (!data.lift_controls || data.lift_controls.length === 0) return '';
      const currentLiftControl = props['_lift_control'] || props['Lift Control'] || '';
      const parentParam = this._resolveControlsParent(data.lift_controls, currentLiftControl);
      const currentSub = data.lift_controls.some(lc => lc.param === currentLiftControl) ? '' : currentLiftControl;
      return this._renderCardGroup('Lift Control', data.lift_controls, parentParam, 'controls', currentSub, true);
    }

    _fFinials(data, props) {
      if (!data.finials || data.finials.length === 0) return '';
      const current = props['Finial Style'] || props['_finial'] || '';
      const sub = props['_finial_sub'] || '';
      return this._renderCardGroup('Finial Style', data.finials, current, 'finial', sub);
    }

    _fSwag(data, props) {
      if (!data.swag_styles || data.swag_styles.length === 0) return '';
      const current = props['Swag Style'] || props['_swag'] || '';
      return this._renderCardGroup('Swag Style', data.swag_styles, current, 'swag');
    }

    // Tieback (non-sheer custom family, mirrors PDP tieback_option metaobject)
    _fTiebacks(data, props) {
      if (!data.tiebacks || !data.tiebacks.length) return '';
      const current = props['_tieback'] || props['Tieback'] || '';
      return this._renderCardGroup('Tieback', data.tiebacks, current, 'tieback');
    }

    // Non-color Shopify variant options (pillow-cover: Size, etc.) — mirrors v4 radio_box options.
    _fVariantOptions(data) {
      if (!data.variant_options || !data.variant_options.length) return '';
      const currentOpts = this._getCurrentVariantOptions(data);
      return data.variant_options.map(opt => {
        const current = currentOpts[opt.index] || '';
        const btns = opt.values.map(val => {
          const isSel = val === current;
          return `<button type="button" class="cdv2-variant-opt-btn${isSel ? ' is-selected' : ''}" data-variant-opt-index="${opt.index}" data-variant-opt-value="${this._esc(val)}">${this._esc(val)}</button>`;
        }).join('');
        return `
          <div class="cdv2-edit-group" data-group="variant-opt-${opt.index}">
            <div class="cdv2-edit-label">${this._esc(opt.name)}<span id="cdv2-vopt-${opt.index}-selected">${this._esc(current)}</span></div>
            <div class="cdv2-variant-opt-row">${btns}</div>
          </div>`;
      }).join('');
    }

    // Get current option values array for the active variant (used for multi-option variant matching).
    _getCurrentVariantOptions(data) {
      const vid = this.pendingChanges.variantId;
      if (data.all_variants && data.all_variants.length) {
        const found = data.all_variants.find(v => v.id === vid);
        if (found) return [...found.opts];
      }
      return data.current_options ? [...data.current_options] : [];
    }

    // Find a variant whose opts array matches the given combination (null slots are ignored).
    _findVariantByOptions(data, opts) {
      if (!data.all_variants || !data.all_variants.length) return null;
      return data.all_variants.find(v => {
        for (let i = 0; i < opts.length; i++) {
          if (opts[i] == null) continue;
          if (v.opts[i] !== opts[i]) return false;
        }
        return true;
      }) || null;
    }

    // Extra/additional options: trim_type, lining_option, roller_type, etc.
    // lift_control is always skipped — it has its own dedicated _fControls renderer.
    _fExtraOptions(data, props) {
      if (!data.extra_options || !data.extra_options.length) return '';
      const parts = [];
      for (const group of data.extra_options) {
        if (group.option_id === 'lift_control') continue;
        if (!group.items || !group.items.length) continue;
        const type = `extra-${group.option_id}`;
        const current = props[group.prop_key] || '';

        // Resolve current to parent param + sub param (subs use compound params)
        let currentParent = current;
        let currentSub = '';
        for (const item of group.items) {
          if (item.param === current) {
            currentParent = item.param;
            currentSub = '';
            break;
          }
          if (item.subs) {
            const matchedSub = item.subs.find(s => s.param === current);
            if (matchedSub) {
              currentParent = item.param;
              currentSub = current;
              break;
            }
          }
        }

        parts.push(this._renderCardGroup(group.label, group.items, currentParent, type, currentSub, true, true));
      }
      return parts.join('');
    }

    // LOCAL: compute per-card option prices (no API).
    // Mirrors PDP behavior:
    //   - v3/v6/v7 (custom-track*): dimension-independent for all options except track_type
    //   - v5 (shades): lift_control dimension-independent; lining_option + trim_type
    //     require width+length; roller_type is not priced
    async _loadExtraOptionPrices(data, props) {
      const hasExtra = data.extra_options && data.extra_options.length;
      const hasControls = data.lift_controls && data.lift_controls.length;
      if (!hasExtra && !hasControls) return;

      const priceTemplate = data.price_template;
      if (!priceTemplate) return;

      const isShades = (data.template || '').includes('shades');
      const localCurrency = await this._getLocalCurrency();
      const panel = this.panel;

      const applyPrices = (params, prices) => {
        prices.forEach((price, i) => {
          const param = params[i];
          if (!param) return;
          const label = price === '0.00' ? '(+$0)' : `(+$${price})`;
          const els = panel ? panel.querySelectorAll(`.${CSS.escape(param)}-price`) : [];
          els.forEach(el => { el.textContent = price != null ? label : ''; });
        });
      };

      const computeBatch = (optionId, params, ctx) => {
        try {
          const csv = params.join(',');
          const result = window.PriceCalc.batchCustomOption(priceTemplate, optionId, csv, ctx, localCurrency);
          return result.prices;
        } catch (e) {
          console.warn('[CartEditor] optionPrice error', optionId, e);
          return null;
        }
      };

      // Shades: lift control prices (dimension-independent)
      if (isShades && hasControls) {
        const parentParams = data.lift_controls.filter(lc => lc.param).map(lc => lc.param);
        if (parentParams.length) {
          const prices = computeBatch('lift_control', parentParams, { width: 0, length: 0, paramHeader: 0 });
          if (prices) applyPrices(parentParams, prices);
        }
      }

      if (!hasExtra) return;

      const isCustomCurtain = ['custom','custom-door','custom-cafe'].includes(data.template || '');

      for (const group of data.extra_options) {
        if (group.option_id === 'lift_control') continue;
        if (group.option_id === 'track_type') continue;
        if (group.option_id === 'rod_type') continue;
        if (group.option_id === 'roller_type') continue; // v5 PDP does not price roller_type
        if (isCustomCurtain && group.option_id === 'trim') continue; // custom: trim uses local formula via _loadCustomCurtainOptionPrices
        if (!group.items || !group.items.length) continue;

        // For items with sub-options: use sub params for price lookup (prices are per-sub).
        const hasSubs = group.items.some(it => it.has_sub && it.subs && it.subs.length);
        let priceParams;
        if (hasSubs) {
          priceParams = group.items.flatMap(it =>
            (it.has_sub && it.subs && it.subs.length)
              ? it.subs.filter(s => s.param).map(s => s.param)
              : (it.param ? [it.param] : [])
          );
        } else {
          priceParams = group.items.filter(it => it.param).map(it => it.param);
        }
        if (!priceParams.length) continue;

        // Shades lining_option + trim_type: dimension-dependent (mirrors v5 getOptionSizePrices)
        let ctx = { width: 0, length: 0, paramHeader: 0 };
        if (isShades && (group.option_id === 'lining_option' || group.option_id === 'trim_type')) {
          const w = parseFloat(props['_width'] ?? props['Width'] ?? '');
          const l = parseFloat(props['_length'] ?? props['Length'] ?? '');
          if (!w || !l) continue;
          ctx = { width: w, length: l, paramHeader: 0 };
        }

        const prices = computeBatch(group.option_id, priceParams, ctx);
        if (prices) applyPrices(priceParams, prices);
      }
    }

    // Local per-card price labels for custom curtain family (mirrors product-custom-variants:
    //   getLiningPrices / getDesignPrices / getTrimPrices — all use priceTemplate formulas, no API)
    async _loadCustomCurtainOptionPrices(data, props) {
      const tmpl = data.template || '';
      if (!['custom','custom-door','custom-cafe'].includes(tmpl)) return;

      const priceTemplate = data.price_template;
      if (!priceTemplate) return;

      const rawW = this._extractNum(props['_width'] || '');
      const rawL = this._extractNum(props['_length'] || '');
      if (!rawW || !rawL) return;

      const w = parseFloat(rawW);
      const l = parseFloat(rawL);
      const header = props['_header'] || '';
      const paramHeader = header ? parseFloat(priceTemplate.custom_curtain?.header?.[header] || 0) : 0;
      const per = parseFloat(priceTemplate.per || 1);

      const exchangeRate = await this._getExchangeRate();
      const panel = this.panel;

      const fmtPrice = (rawPrice) => {
        const p = parseFloat((rawPrice * exchangeRate).toFixed(2));
        if (p < 0) return '';
        return p === 0 ? '(+$0)' : `(+$${p.toFixed(2)})`;
      };

      // Lining per-card prices
      if (data.linings && data.linings.length && priceTemplate.custom_curtain?.lining) {
        for (const lining of data.linings) {
          const paramLining = parseFloat(priceTemplate.custom_curtain.lining[lining.param]);
          if (isNaN(paramLining)) continue;
          const price = _curtainCalcLining(w, l, paramHeader, paramLining, priceTemplate);
          const label = fmtPrice(price);
          const els = panel ? panel.querySelectorAll(`.${CSS.escape(lining.param)}-price`) : [];
          els.forEach(el => { el.textContent = label; });
        }
      }

      // Design per-card prices
      if (data.design_options && data.design_options.length && priceTemplate.custom_curtain?.design) {
        for (const design of data.design_options) {
          const paramDesign = parseFloat(priceTemplate.custom_curtain.design[design.param]);
          if (isNaN(paramDesign)) continue;
          const price = _curtainCalcDesign(w, l, design.param, paramHeader, paramDesign);
          const label = fmtPrice(price);
          const els = panel ? panel.querySelectorAll(`.${CSS.escape(design.param)}-price`) : [];
          els.forEach(el => { el.textContent = label; });
        }
      }

      // Trim per-card prices (extra option 'trim', compound params e.g. trim_roman_02-25_black)
      const trimGroup = (data.extra_options || []).find(g => g.option_id === 'trim');
      if (trimGroup && trimGroup.items && priceTemplate.custom_option?.trim_type) {
        const hasSubs = trimGroup.items.some(it => it.has_sub && it.subs && it.subs.length);
        const trimParams = hasSubs
          ? trimGroup.items.flatMap(it =>
              (it.has_sub && it.subs && it.subs.length)
                ? it.subs.filter(s => s.param).map(s => s.param)
                : (it.param ? [it.param] : [])
            )
          : trimGroup.items.filter(it => it.param).map(it => it.param);

        for (const param of trimParams) {
          let price = 0;
          if (param && param !== 'none') {
            const parts = param.split('-');
            if (parts.length >= 2) {
              const trimType = parts[0];
              const trimMaterial = parts.slice(1).join('-');
              const basePrice = parseFloat(priceTemplate.custom_option.trim_type[trimMaterial] || 0);
              price = _curtainCalcTrim(w, l, paramHeader, trimType, basePrice, per);
            }
          }
          const label = fmtPrice(price);
          const els = panel ? panel.querySelectorAll(`.${CSS.escape(param)}-price`) : [];
          els.forEach(el => { el.textContent = label; });
        }
      }
    }

    // LOCAL: Valance (v11) lining prices — mirrors PDP getLiningPrices
    async _loadValanceLiningPrices(data, props) {
      if ((data.template || '') !== 'custom-valance') return;
      if (!data.linings || !data.linings.length) return;
      const w = parseFloat(props['_width'] || '');
      const l = parseFloat(props['_length'] || '');
      const header = props['_header'] || '';
      if (!w || !l || !header) return;

      const priceTemplate = data.price_template;
      if (!priceTemplate || !priceTemplate.custom_curtain) return;

      try {
        const localCurrency = await this._getLocalCurrency();
        const paramHeader = parseFloat(priceTemplate.custom_curtain.header?.[header]) || 0;
        const params = data.linings.filter(li => li.param).map(li => li.param);
        const paramLinings = params.map(p => parseFloat(priceTemplate.custom_curtain.lining?.[p]) || 0);
        const prices = window.PriceCalc.calcLiningExtraPrices(w, l, paramHeader, paramLinings, priceTemplate, localCurrency);
        const panel = this.panel;
        prices.forEach((price, i) => {
          const param = params[i];
          if (!param || price == null) return;
          const formatted = price.toFixed(2);
          const label = (price === 0 || formatted === '0.00') ? '(+$0)' : `(+$${formatted})`;
          const els = panel ? panel.querySelectorAll(`.${CSS.escape(param)}-price`) : [];
          els.forEach(el => { el.textContent = label; });
        });
      } catch (e) {
        console.warn('[CartEditor] valance lining price error', e);
      }
    }

    // Design options: per-variant (custom family, from variant.design_options_to_be_included)
    _fDesignOptions(data, props) {
      if (!data.design_options || !data.design_options.length) return '';
      const current = props['_design'] || props['Design'] || '';
      // Custom curtain family: per-card prices calculated locally (mirrors product-custom-variants getDesignPrices)
      const showPrices = ['custom','custom-door','custom-cafe'].includes(data.template || '');
      return this._renderCardGroup('Design', data.design_options, current, 'design', '', showPrices);
    }

    _resolveControlsParent(liftControls, currentLiftControl) {
      if (!currentLiftControl) return '';
      const direct = liftControls.find(lc => lc.param === currentLiftControl || lc.title === currentLiftControl);
      if (direct) return direct.param;
      for (const lc of liftControls) {
        if (lc.subs && lc.subs.some(s => s.param === currentLiftControl)) return lc.param;
      }
      return currentLiftControl;
    }

    _renderBendNoteField(props, caps) {
      if (!caps || !caps.bend_note) return '';
      const val = this._esc(props['Bend Note'] || '');
      return `
        <div class="cdv2-edit-group" data-group="bend-note">
          <div class="cdv2-edit-label">Bend Note</div>
          <p class="cdv2-room-in-size-hint" style="font-size:var(--fs-meta, 1rem);color:#9b958c;margin:0 0 6px;">
            Describe the bend position and angle (required).
          </p>
          <input
            type="text"
            class="cdv2-room-in-size-input"
            id="cdv2-field-bend_note"
            value="${val}"
            placeholder="e.g. 90° bend at 120cm from left"
            data-text-key="Bend Note"
            autocomplete="off"
          >
          <p id="cdv2-bend-note-error" class="cdv2-field-error" style="display:none;color:#c84040;font-size:var(--fs-meta, 1rem);margin:4px 0 0;"></p>
        </div>
      `;
    }

    _renderFabricField(props) {
      const val = this._esc(props['Fabric'] || props['fabric'] || '');
      return `
        <div class="cdv2-edit-group" data-group="fabric">
          <div class="cdv2-edit-label">Fabric</div>
          <input
            type="text"
            class="cdv2-fabric-input"
            data-text-key="Fabric"
            value="${val}"
            placeholder="Enter fabric description"
            aria-label="Fabric"
          />
        </div>
      `;
    }

    _renderHeaderReadOnly(props, data) {
      const parent = this._getProp(props, 'Header', '_header');
      const sub = props['_header_sub'];
      let display = parent || '';
      if (sub) display = display ? `${display} — ${sub}` : sub;
      return `
        <div class="cdv2-edit-group" data-group="header-readonly">
          <div class="cdv2-edit-label">
            Header
            <span id="cdv2-header-selected">${this._esc(display)}</span>
          </div>
          <p class="cdv2-header-readonly-hint">
            Need to tweak your header?
            <a href="#live-chat" class="cdv2-inline-link">Let's chat.</a>
          </p>
        </div>
      `;
    }

    _pluralQtyPhrase(qty) {
      const sg = (this.activeData && this.activeData.qty_unit_singular) || 'Piece';
      const q = Number(qty) || 1;
      if (q === 1) return `${q} ${sg}`;
      const lower = String(sg).toLowerCase();
      let pl = sg;
      if (lower === 'panel') pl = 'Panels';
      else if (lower === 'piece') pl = 'Pieces';
      else if (lower.endsWith('y') && !/[aeiou]y$/i.test(String(sg))) pl = String(sg).slice(0, -1) + 'ies';
      else if (/[sxz]$/i.test(lower) || /ch$/i.test(lower) || /sh$/i.test(lower)) pl = `${sg}es`;
      else if (!/s$/i.test(String(sg))) pl = `${sg}s`;
      return `${q} ${pl}`;
    }

    _summarySizeLine(props) {
      if (!this.activeData) return '';
      const caps = resolveTemplateCaps(this.activeData.template);
      if (!caps.dimensions) return '';
      const tmpl = this.activeData.template || '';
      const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';
      const isCm = isDimCm(this.activeData, props);
      // For cm: show cm value from display props; for inches: use _width (stored as inches)
      let w, l;
      if (isCm) {
        // Rod Width may include suffix like "(X=44, Y=66)" — extract leading number only
        const wDisp = props['Width'] || props['Track Width'] || props['Rod Width'] || '';
        const wm = String(wDisp).match(/^(\d+(?:\.\d+)?)/);
        w = wm ? parseFloat(wm[1]) : '';
        l = this._extractNum(props['Length'] || '');
      } else {
        // Track-joe inch: Track Width display already has fraction format like "65 1/4""
        if (tmpl === 'custom-track-joe') {
          const dispW = props['Track Width'] || '';
          w = dispW ? dispW.replace(/"$/, '') : this._extractNum(this._getProp(props, '_track_width', '_width'));
        } else {
          w = this._extractNum(this._getProp(props, '_track_width', '_width', 'Width', 'width', 'WIDTH'));
        }
        l = this._extractNum(this._getProp(props, '_length', 'Length', 'length', 'LENGTH'));
      }
      if (w === '' && l === '') return '';
      const wf = w === '' ? '—' : (isCm ? `${w} cm` : (tmpl === 'custom-track-joe' ? `${w}"` : `${w}"`));
      if (isTrack) {
        const widthLabel = ('Rod Width' in props) ? 'rod width' : 'track width';
        return `${wf} (${widthLabel})`;
      }
      const lf = l === '' ? '—' : (isCm ? `${l} cm` : `${l}"`);
      return `${wf} W × ${lf} L (per panel)`;
    }

    _selectedSummaryRows(props, caps, data) {
      const rows = [];
      const add = (label, val) => {
        const v = val != null && String(val).trim() !== '' ? String(val).trim() : '';
        if (v) rows.push({ label, value: v });
      };

      if (caps.colors && data.has_color_option !== false && data.colors && data.colors.length > 1) {
        let colorLabel = '';
        // Multi-option products: look up color from current variant's options array
        if (data.all_variants && data.all_variants.length && data.color_option_index != null) {
          const currentOpts = this._getCurrentVariantOptions(data);
          colorLabel = currentOpts[data.color_option_index] || '';
        }
        if (!colorLabel) {
          const c = data.colors.find(x => x.variant_id === this.pendingChanges.variantId);
          if (c) colorLabel = c.label;
        }
        if (colorLabel) add('Color', colorLabel);
      }

      // Non-color variant options (pillow-cover: Size etc.)
      if (data.variant_options && data.variant_options.length) {
        const currentOpts = this._getCurrentVariantOptions(data);
        for (const opt of data.variant_options) {
          const val = currentOpts[opt.index];
          if (val) add(opt.name, val);
        }
      }

      if (caps.headers) {
        let h = this._getProp(props, 'Header', '_header');
        const sub = props['_header_sub'];
        if (sub) h = h ? `${h} / ${sub}` : sub;
        add('Header', h);
      }

      if (caps.mounts) {
        const m = this._getProp(props, 'Mount Type', '_mount_type', '_mount');
        const ms = props['_mount_sub'];
        add('Mount Type', ms ? `${m} / ${ms}` : m);
      }

      if (caps.controls) {
        const lc = props['Lift Control'] || props['_lift_control'] || '';
        add('Lift Control', lc);
      }

      // Note: finials are now rendered via extra_options for custom-track (see below)
      // caps.swag removed — custom-swag now uses design_options (see below)

      if (isRexTrack(data)) {
        const wt = props['Width Type'] || '';
        if (wt) add('Width Type', wt);
      }
      /* 卷帘与 PDP 汇总块同口径：内装列 Ordered/Finished 四项，外装回到 Width / Length，
         不再用 "66" W × 55" L (per panel)" 那种合并行。
         值直接取行属性 —— syncShadeOrderedFinished 已保证它们跟着尺寸和安装方式走，
         这里再拼一遍就等于第二套取整口径。
         四项不齐就走 Width/Length：外装本来就没有减宽，改版前加购的行也没有这四项。 */
      if ((data.template || '').includes('shades') && caps.dimensions) {
        const _ow = props['Ordered Width'], _ol = props['Ordered Length'];
        const _fw = props['Finished Width'], _fl = props['Finished Length'];
        if (_ow && _ol && _fw && _fl) {
          add('Ordered Width', _ow);
          add('Ordered Length', _ol);
          add('Finished Width', _fw);
          add('Finished Length', _fl);
        } else {
          add('Width', props['Width']);
          add('Length', props['Length']);
        }
      } else {
        const sizeLine = this._summarySizeLine(props);
        if (sizeLine) add('Size', sizeLine);
      }

      if (caps.linings) {
        let lin = this._getProp(props, 'Lining', '_lining');
        const lsub = props['_lining_sub'];
        if (lsub) lin = lin ? `${lin} / ${lsub}` : lsub;
        add('Lining', lin);
      }

      // Extra options (trim_type, etc.) — custom family
      // lift_control is excluded: already shown via caps.controls above
      if (data.extra_options && data.extra_options.length) {
        for (const grp of data.extra_options) {
          if (grp.option_id === 'lift_control') continue;
          const val = props[grp.prop_key] || '';
          if (!val || val === 'none') continue;
          let display = val;
          for (const item of (grp.items || [])) {
            if (item.param === val) { display = item.title; break; }
            const sub = (item.subs || []).find(s => s.param === val);
            if (sub) { display = `${item.title} / ${sub.title}`; break; }
          }
          add(grp.label, display);
        }
      }

      // Design option — custom family (per-variant)
      if (data.design_options && data.design_options.length) {
        const dVal = props['_design'] || props['Design'] || '';
        if (dVal) {
          const dItem = (data.design_options || []).find(d => d.param === dVal);
          add('Design', dItem ? dItem.title : dVal);
        }
      }

      // Tieback — non-sheer custom family (last step in PDP)
      if (data.tiebacks && data.tiebacks.length) {
        add('Tieback', this._getProp(props, 'Tieback', '_tieback'));
      }

      if (caps.fabric) {
        add('Fabric', this._getProp(props, 'Fabric', 'fabric'));
      }

      add('Weight', this._getProp(props, 'Weight', '_weight'));
      add('Pleat', this._getProp(props, 'Pleat', 'Pleats', '_pleats', 'pleats'));

      if (caps.bend_note) {
        const bn = props['Bend Note'] || '';
        if (bn) add('Bend Note', bn);
      }

      if (caps.room) {
        const rn = this._getProp(props, 'Room Name', 'room_name');
        if (rn) add('Room Name', rn);
      }

      add('Quantity', this._pluralQtyPhrase(this.pendingChanges.qty));

      return rows;
    }

    _updateSelectedSummary() {
      const box = $('#cdv2-editor-selected', this.panel);
      if (!box || !this.activeData) return;
      const caps = resolveTemplateCaps(this.activeData.template);
      const props = this.pendingChanges.properties || {};
      const rows = this._selectedSummaryRows(props, caps, this.activeData);
      if (!rows.length) {
        box.innerHTML = '';
        box.hidden = true;
        return;
      }
      box.hidden = false;
      const inner = rows.map(r => `
        <div class="cdv2-editor-selected-row">
          <span class="cdv2-editor-selected-key">${this._esc(r.label)}</span>
          <span class="cdv2-editor-selected-val">${this._esc(r.value)}</span>
        </div>
      `).join('');
      box.innerHTML = `
        <p class="cdv2-editor-selected-title">You've selected</p>
        <div class="cdv2-editor-selected-rows">${inner}</div>
      `;
    }

    /* ── Sync SIZE step-header label (mirrors PDP "44"W x 66"L" display) ── */
    _updateDimensionLabel() {
      if (!this.panel || !this.activeData) return;
      const el = this.panel.querySelector('#cdv2-dimensions-selected');
      if (!el) return;
      const props = this.pendingChanges.properties || {};
      const data = this.activeData;
      const tmpl = data.template || '';
      // Rex: Rod Width label is maintained by syncRexDimProps via props['Rod Width']
      if (tmpl === 'custom-track-rex') {
        el.textContent = props['Rod Width'] || '';
        return;
      }
      const isCm = isDimCm(data, props);
      const unit = isCm ? 'cm ' : '"';
      const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';
      const w = getEditableWidthValue(props, data);
      const l = isTrack ? '' : getEditableLengthValue(props, data);
      let label = '';
      if (isTrack) {
        if (w !== '' && w !== null) label = `${w}${unit}`;
      } else {
        if (w !== '' && w !== null && l !== '' && l !== null) {
          label = `${w}${unit}W x ${l}${unit}L`;
        }
      }
      el.textContent = label;
    }

    _updateDepthVisibility() {
      if (!this.panel || !this.activeData) return;
      const tmpl = this.activeData.template || '';
      if (tmpl !== 'custom-valance' && tmpl !== 'custom-swag') return;
      const row = this.panel.querySelector('[data-depth-row]');
      if (!row) return;
      const props = this.pendingChanges.properties || {};
      const mountType = props['Mount Type'] || '';
      const show = mountType.includes('Velcro for Board Mounted');
      row.hidden = !show;
      if (!show) delete props['Depth'];
    }

    /* ── v6 (custom-track-rex): hide "Ceiling Mounted" when runner_type is rings ── */
    _applyRexMountingConstraint() {
      if (!this.panel || !this.activeData) return;
      if (this.activeData.template !== 'custom-track-rex') return;
      const props = this.pendingChanges.properties || {};
      const isRings = props._runner_type === 'rings';
      let cleared = false;
      const cards = this.panel.querySelectorAll('[data-opt-type="extra-mounting_type"]');
      cards.forEach(card => {
        if (card.dataset.optTitle !== 'Ceiling Mounted') return;
        card.style.display = isRings ? 'none' : '';
        if (isRings && card.classList.contains('is-selected')) {
          card.classList.remove('is-selected');
          const group = (this.activeData.extra_options || []).find(g => g.option_id === 'mounting_type');
          if (group) delete props[group.prop_key];
          const lbl = this.panel.querySelector('#cdv2-extra-mounting_type-selected');
          if (lbl) lbl.textContent = '';
          cleared = true;
        }
      });
      // If mounting type was just cleared, require user to pick a valid one before saving
      if (cleared) {
        this._requiredOptionsValid = false;
        this._updateSaveState();
      }
    }

    /* ── v7 (custom-track-roman): hide "Inside Mount" past 70" of rod width ──
       Mirrors the PDP rule in product-variants-v7.liquid (johnInsideMountBlocked).
       Driven by the dimension rather than by an option click, so unlike the rex
       constraint above it is re-applied from _markDirty() on every edit. */
    _applyRomanInsideMountConstraint() {
      if (!this.panel || !this.activeData) return;
      if ((this.activeData.template || '') !== 'custom-track-roman') return;

      const MAX_WIDTH_IN = 70;
      const INSIDE_MOUNT_PARAM = 'john_inside_mount';

      const props = this.pendingChanges.properties || {};
      // _track_width is always inches here: syncDimensionProps() converts cm on the
      // way in, the same rounding the PDP applies before add-to-cart.
      const widthIn = parseFloat(props._track_width);
      const tooWide = !Number.isNaN(widthIn) && widthIn > MAX_WIDTH_IN;

      let cleared = false;
      const cards = this.panel.querySelectorAll('[data-opt-type="extra-mounting_type"]');
      cards.forEach(card => {
        if (card.dataset.optParam !== INSIDE_MOUNT_PARAM) return;
        card.style.display = tooWide ? 'none' : '';
        if (tooWide && card.classList.contains('is-selected')) {
          card.classList.remove('is-selected');
          const group = (this.activeData.extra_options || []).find(g => g.option_id === 'mounting_type');
          if (group) delete props[group.prop_key];
          const lbl = this.panel.querySelector('#cdv2-extra-mounting_type-selected');
          if (lbl) lbl.textContent = '';
          cleared = true;
        }
      });
      // Same as rex: force the user to pick a valid mounting type before saving.
      if (cleared) {
        this._requiredOptionsValid = false;
        this._updateSaveState();
      }
    }

    /* ── Color swatches group ───────────────────────────────── */
    _renderColorGroup(colors, activeVariantId) {
      const swatches = colors.map(c => {
        const isSelected = c.variant_id === activeVariantId;
        const style = c.swatch_img
          ? `background-image:url('${this._esc(c.swatch_img)}');background-size:cover;`
          : c.swatch_hex ? `background:${this._esc(c.swatch_hex)};` : 'background:#e8e4d6;';
        return `
          <button
            type="button"
            class="cdv2-color-opt ${isSelected ? 'is-selected' : ''} ${!c.available ? 'is-oos' : ''}"
            data-variant-id="${c.variant_id}"
            data-color-label="${this._esc(c.label)}"
            title="${this._esc(c.label)}"
            ${!c.available ? 'disabled' : ''}
          >
            <span class="cdv2-color-chip" style="${style}"></span>
          </button>
        `;
      }).join('');

      return `
        <div class="cdv2-edit-group" data-group="colors">
          <div class="cdv2-edit-label">
            Color
            <span id="cdv2-color-selected">${this._esc(colors.find(c => c.variant_id === activeVariantId)?.label || '')}</span>
          </div>
          <div class="cdv2-color-grid">${swatches}</div>
        </div>
      `;
    }

    /* ── Image card group (headers / linings / mounts / finials) ── */
    // currentValue  = display title or backend param for the PARENT option
    // currentSubValue = backend param for the SUB option (e.g. _header_sub)
    // showPriceSpans = true to render a price label span per card (for extra options)
    // showSubPriceSpans = false to suppress price spans on sub-option cards (e.g. runner_type)
    _renderCardGroup(label, items, currentValue, type, currentSubValue = '', showPriceSpans = false, showSubPriceSpans = true) {
      // Trim's sub-step is titled "Trim Material" on the PDP (product-variants-v5) —
      // keep the same wording here and in _subLabelFor.
      const isTrim = type === 'extra-trim_type' || type === 'extra-trim';
      const subHeading = (item) => item.subcategory_title || (isTrim ? 'Trim Material' : 'Choose style');
      const cards = items.map(item => {
        // Match by display title OR backend param
        const isSelected = item.title === currentValue || item.param === currentValue;
        const hasSubs = item.has_sub && item.subs && item.subs.length > 0;
        // Parent cards with sub-options don't show price (price lives on sub cards)
        const priceSpan = showPriceSpans && item.param && !hasSubs
          ? `<span class="cdv2-opt-price ${this._esc(item.param)}-price"></span>`
          : '';
        return `
          <button
            type="button"
            class="cdv2-opt-card ${isSelected ? 'is-selected' : ''}"
            data-opt-type="${type}"
            data-opt-title="${this._esc(item.title)}"
            data-opt-param="${this._esc(item.param || item.title)}"
            data-has-sub="${hasSubs}"
            title="${this._esc(item.title)}"
          >
            <span class="cdv2-opt-visual" style="${item.image_url ? `background-image:url('${this._esc(item.image_url)}');background-size:cover;` : ''}"></span>
            <strong>${this._esc(item.title)}</strong>
            ${priceSpan}
          </button>
        `;
      }).join('');

      // Sub-options panel: visible only when its parent is selected
      const subPanels = items
        .filter(item => item.has_sub && item.subs && item.subs.length > 0)
        .map(item => {
          // Is THIS parent currently selected?
          const isParentSelected = item.title === currentValue || item.param === currentValue;

          const subCards = item.subs.map(sub => {
            // Pre-select sub via currentSubValue (backend param)
            const isSub = sub.param === currentSubValue || sub.title === currentSubValue;
            const subPriceSpan = showPriceSpans && showSubPriceSpans && sub.param
              ? `<span class="cdv2-opt-price ${this._esc(sub.param)}-price"></span>`
              : '';
            return `
              <button
                type="button"
                class="cdv2-opt-card ${isSub ? 'is-selected' : ''}"
                data-opt-type="${type}-sub"
                data-parent-param="${this._esc(item.param || item.title)}"
                data-opt-title="${this._esc(sub.title)}"
                data-opt-param="${this._esc(sub.param)}"
                title="${this._esc(sub.title)}"
              >
                <span class="cdv2-opt-visual" style="${sub.image_url ? `background-image:url('${this._esc(sub.image_url)}');background-size:cover;` : ''}"></span>
                <strong>${this._esc(sub.title)}</strong>
                ${subPriceSpan}
              </button>
            `;
          }).join('');

          return `
            <div
              class="cdv2-sub-panel"
              data-parent-param="${this._esc(item.param || item.title)}"
              ${isParentSelected ? '' : 'style="display:none;"'}
            >
              <p style="margin:8px 0 10px;color:#9b958c;font-size:var(--fs-meta, 1rem);font-weight: 500;text-transform:uppercase;letter-spacing:0.06em;">
                ${this._esc(subHeading(item))}
              </p>
              <div class="cdv2-card-grid cols-4">${subCards}</div>
            </div>
          `;
        }).join('');

      // Display label: prefer title match; append sub title when selected
      const selectedItem = items.find(i => i.title === currentValue || i.param === currentValue);
      let displayLabel = selectedItem ? selectedItem.title : currentValue;
      if (currentSubValue && selectedItem && selectedItem.subs) {
        const selectedSub = selectedItem.subs.find(s => s.param === currentSubValue || s.title === currentSubValue);
        if (selectedSub) displayLabel = `${displayLabel}(${selectedSub.title})`;
      }

      return `
        <div class="cdv2-edit-group" data-group="${type}">
          <div class="cdv2-edit-label">
            ${this._esc(label)}
            <span id="cdv2-${type}-selected">${this._esc(displayLabel)}</span>
          </div>
          <div class="cdv2-card-grid" data-card-type="${type}">${cards}</div>
          ${subPanels}
          ${subPanels ? `<p class="cdv2-sub-error" data-sub-error="${type}" hidden></p>` : ''}
        </div>
      `;
    }

    /* ── Normalize Shopify properties to a plain object ────────
     * Shopify may return properties as:
     *   - JSON object {"_width":"52",...}          (Liquid | json filter)
     *   - Array  [["_width","52"],...]             (cart.js API)
     *   - Mixed  {0:["_width","52"],"_width":"52"} (after a bad editor save)
     */
    _parseProperties(raw) {
      if (!raw) return {};
      if (Array.isArray(raw)) {
        const obj = {};
        for (const entry of raw) {
          if (Array.isArray(entry) && entry.length >= 2) {
            obj[String(entry[0])] = entry[1];
          }
        }
        return obj;
      }
      if (typeof raw === 'object') {
        const obj = {};
        for (const [k, v] of Object.entries(raw)) {
          // Numeric keys with [key, value] pairs: the old array-in-object format
          if (!isNaN(Number(k)) && Array.isArray(v) && v.length >= 2) {
            // Only add if the named key doesn't already exist (named wins)
            if (!(String(v[0]) in obj)) obj[String(v[0])] = v[1];
          } else {
            obj[k] = v;
          }
        }
        return obj;
      }
      return raw;
    }

    /* ── Case-insensitive property lookup with multiple key fallbacks ── */
    _getProp(props, ...keys) {
      for (const key of keys) {
        const val = props[key];
        if (val !== undefined && val !== null && val !== '') return val;
        // Try case-insensitive match
        const lk = key.toLowerCase();
        for (const k of Object.keys(props)) {
          if (k.toLowerCase() === lk) {
            const v = props[k];
            if (v !== undefined && v !== null && v !== '') return v;
          }
        }
      }
      return '';
    }

    /* ── Extract numeric dimension value from a property that may contain units ── */
    _extractNum(v) {
      if (typeof v === 'number' && !isNaN(v)) return v;
      const m = String(v || '').match(/[\d.]+/);
      return m ? parseFloat(m[0]) : '';
    }

    _applyDerivedPropsBeforeSave() {
      if (!this.activeData) return;
      const tmpl = this.activeData.template || '';
      const props = this.pendingChanges.properties || {};

      // 卷帘 Ordered/Finished 四项的兜底重算。syncDimensionProps 与 mount 分支各调过一次，
      // 这里再来一遍是为了覆盖其他任何改到 Width/Length/Mount Type 的路径 ——
      // 按本方法下面那段注释的教训，陈旧的派生属性比没有这条属性更糟。
      syncShadeOrderedFinished(props, this.activeData);

      // 幔头 / swag（custom-valance / custom-swag）**不在此列**：它们的产品页是
      // snippets/product-variants-v11 / v12，既不算褶子数也不算重量（两个文件里
      // pleat 与 setWeight 均为 0 处），真实订单上也从来没有 Pleat / Weight 属性。
      // 早先把它们算进来，会让客服在购物车编辑器里一保存就凭空加上产品页从不产生的
      // 属性（"Pleat Valance" 这个 Header 含 "Pleat"，会命中 /5.12 分支），
      // 工厂单上多出一条本不存在的生产指令。
      const isCurtainFamily = ['custom', 'custom-door', 'custom-cafe'].includes(tmpl);
      if (!isCurtainFamily) {
        // 历史残留清理：2026-05-29（6d5203e）到 2026-09-15 期间 custom-valance /
        // custom-swag 曾被误算进上面的名单，这段时间客服在编辑器里保存过的幔头行
        // 可能已经被写入了 Pleat/Hook/Weight（"Pleat Valance" 这个 Header 会命中
        // /5.12 分支）。而 save() 的 EXCLUDE 只排除 _cart_edit_token/_cart_edit_source，
        // 其余属性原样提交，所以这些残留值会在之后每次保存时被原封不动写回、
        // 且永远不再随宽度/帘头更新 —— 比没有这条属性更糟。这里主动清掉。
        if (tmpl === 'custom-valance' || tmpl === 'custom-swag') {
          delete props['Pleat'];
          delete props['Hook'];
          delete props['Weight'];
        }
        return;
      }

      const header = this._getProp(props, 'Header', '_header');
      // _width/_length always store inches (PDP buildProperties converts cm→in before cart add)
      const widthRaw = this._extractNum(props['_width'] || '');
      const lengthRaw = this._extractNum(props['_length'] || '');
      const isCm = isDimCm(this.activeData, props);
      const widthIn = widthRaw || '';

      // Pleat/Hook (best-effort; mirrors PDP formulas)
      if (header && widthIn) {
        let count = '';
        // 咖啡帘褶距更密用 /4；条件必须是 Cafe && Pleat 而非字面量 'Cafe Pinch Pleat'，
        // 否则 'Cafe Euro Pleat Double/Triple' 会掉进 /5.12。与 product-custom-variants.liquid
        // 的 pleatFor() 保持一致，改一处必须同步改另一处。
        if (String(header).includes('Cafe') && String(header).includes('Pleat')) count = Math.round(widthIn / 4 + 1.2);
        else if (String(header).includes('Pleat')) count = Math.round(widthIn / 5.12 + 1.2);
        if (count) {
          if (String(header) === 'Pencil Pleat') {
            count = Math.round(widthIn / 3 + 1.2);
            props['Hook'] = `${count} hooks (per panel)`;
            delete props['Pleat'];
          } else {
            props['Pleat'] = `${count} pleats (per panel)`;
            delete props['Hook'];
          }
        } else {
          delete props['Pleat'];
          delete props['Hook'];
        }
      }

      // Weight (mirrors PDP setWeight): B = fabricWeight + liningWeight (from lining metaobject)
      const fw = this.activeData.fabric_weight;
      if (!fw || !widthRaw || !lengthRaw || !header) return;
      const fabricWeight = this._extractNum(fw);
      if (!fabricWeight) return;

      const lining = this._getProp(props, 'Lining', '_lining') || '';
      // liningWeight: from lining metaobject's weight field (same as PDP handleClickLining)
      const liningMo = (this.activeData.linings || []).find(l => l.title === lining || l.param === lining);
      const liningWeight = liningMo ? (parseFloat(liningMo.weight) || 0) : 0;
      const B = fabricWeight + liningWeight;

      // C: flat-area lining material density (matches PDP — checks backend param now).
      const liningParam = this._getLiningParam(props, this.activeData);
      let C = 0;
      if (liningParam === 'white_lining_roman') C = 140;
      else if (liningParam === 'black_lining') C = 140;
      else if (liningParam === 'triple_weave_lining') C = 220;
      else if (liningParam === 'blackout_lining') C = 120;

      const wIn = parseFloat(widthRaw);
      const lIn = parseFloat(lengthRaw);
      if (!wIn || !lIn) return;

      let weightKg = 0;
      const h = String(header);

      if (h.includes('Grommet Top') || h.includes('Eyelet Top')) {
        weightKg = (wIn * 0.0254 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
          + wIn * 0.0254 * 0.025
          + (Math.floor(wIn * 0.0254 / 0.165) + 1) * 0.01
          + wIn * lIn * 0.0254 * 0.0254 * C / 1000;
      } else if (h === 'Rod Pocket' || h === 'Slot Top') {
        weightKg = (wIn * 0.0254 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
          + wIn * lIn * 0.0254 * 0.0254 * C / 1000;
      } else if (h.includes('Pleat')) {
        if (h.includes('Double')) {
          weightKg = (wIn * 0.0254 + (wIn / 5 + 1) * 0.12 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
            + wIn * 0.0254 * 2 * 0.025
            + wIn * 2 * lIn * 0.0254 * 0.0254 * C / 1000;
        } else {
          weightKg = (wIn * 0.0254 + (wIn / 5 + 1) * 0.18 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
            + wIn * 2.5 * 0.0254 * 0.025
            + wIn * 2.5 * lIn * 0.0254 * 0.0254 * C / 1000;
        }
      } else if (h === 'Back Tab') {
        weightKg = (wIn * 0.0254 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
          + wIn / 6 * B / 1000 * 0.011
          + wIn * lIn * 0.0254 * 0.0254 * C / 1000;
      } else if (h === 'Tab Top') {
        weightKg = (wIn * 0.0254 + 0.15) * ((lIn - 4) * 0.0254 + 0.29) * B / 1000
          + wIn / 6 * B / 1000 * 0.019
          + wIn * lIn * 0.0254 * 0.0254 * C / 1000;
      } else if (h === 'Flat Panel') {
        weightKg = (wIn * 0.0254 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
          + wIn * 0.0254 * 0.02
          + wIn * lIn * 0.0254 * 0.0254 * C / 1000;
      } else if (h.includes('Ripple Fold') || h.includes('Wave')) {
        weightKg = ((wIn * 0.0254 / 0.06 + 1) * 0.125 + 0.15) * (lIn * 0.0254 + 0.29) * B / 1000
          + wIn * 2.2 * 0.0254 * 0.03
          + (wIn * 0.0254 / 0.06 + 1) * 0.125 * lIn * 0.0254 * C / 1000;
      }

      if (weightKg > 0) {
        if (isCm) props['Weight'] = `~${weightKg.toFixed(2)} kg (per panel)`;
        else props['Weight'] = `~${(weightKg / 0.45).toFixed(2)} lbs (per panel)`;
      }
    }

    _renderRoomWithinSize(props, data, caps) {
      if (!caps.room) return '';
      const tmpl = data.template || '';
      const roomName = this._getProp(props, 'Room Name', 'room_name', 'ROOM NAME');
      const placement = data.room_placement || 'after_dims';
      const label = data.room_label || 'Room name';
      const hint = data.room_hint || '';
      const ph = data.room_placeholder || '';
      const usePresets = data.room_use_presets !== false;
      const presets = Array.isArray(data.room_presets) ? data.room_presets : [];
      const listId = `cdv2-room-datalist-${String(data.key || 'x').replace(/[^a-z0-9_-]/gi, '')}`;

      const datalist = usePresets && presets.length
        ? `<datalist id="${this._esc(listId)}">${presets.map(p => `<option value="${this._esc(p)}"></option>`).join('')}</datalist>`
        : '';

      const listAttr = usePresets && presets.length ? ` list="${this._esc(listId)}"` : '';

      return `
        <div class="cdv2-room-in-size" data-room-placement="${this._esc(placement)}">
          <label class="cdv2-room-in-size-label" for="cdv2-field-room_name">${this._esc(label)}</label>
          ${hint ? `<p class="cdv2-room-in-size-hint">${this._esc(hint)}</p>` : ''}
          <input
            type="text"
            id="cdv2-field-room_name"
            class="cdv2-room-in-size-input"
            value="${this._esc(roomName)}"
            placeholder="${this._esc(ph)}"
            data-text-key="room_name"
            autocomplete="off"
            ${listAttr}
          >
          <p id="cdv2-room-name-error" class="cdv2-field-error" style="display:none;color:#c84040;font-size:var(--fs-meta, 1rem);margin:4px 0 0;"></p>
          ${datalist}
        </div>
      `;
    }

    _renderDimFieldRow(dim, label, value, data, props, idSuffix) {
      const isCm = isDimCm(data, props);
      const unitLabel = isCm ? 'cm' : 'inches';
      const shadeFracs = usesShadeFractionSelects(data, props);
      const allowsDec = dimAllowsDecimals(data, props);
      const fracVal = getFractionSelectValue(props, dim);
      const fracOptions = FRACTION_OPTIONS.map((o) =>
        `<option value="${o}"${fracVal === o ? ' selected' : ''}>${o === '0/0' ? '0' : o}</option>`
      ).join('');

      /* Custom PDP: product-custom-variants — type=number step=0.1 + ValidateUtils on input */
      if (allowsDec) {
        return `
            <div class="cdv2-edit-field">
              <label for="cdv2-${idSuffix}">${this._esc(label)}</label>
              <input
                type="number"
                id="cdv2-${idSuffix}"
                name="${dim}"
                value="${this._esc(String(value))}"
                step="0.1"
                min="0"
                data-dim="${dim}"
                data-dim-input="pdp-number"
              >
              <p class="cdv2-dim-error" data-dim-error="${dim}" hidden></p>
            </div>`;
      }

      return `
            <div class="cdv2-edit-field">
              <label for="cdv2-${idSuffix}">${this._esc(label)}</label>
              <input
                type="text"
                id="cdv2-${idSuffix}"
                name="${dim}"
                value="${this._esc(String(value))}"
                inputmode="numeric"
                autocomplete="off"
                data-dim="${dim}"
              >
              <p class="cdv2-dim-error" data-dim-error="${dim}" hidden></p>
            </div>`;
    }

    /* ── Dimensions group (fixed unit from line item; no in/cm toggle) ── */
    _renderDimensionGroup(props, data, caps) {
      const tmpl = data.template || '';
      const isTrack = tmpl.includes('custom-track') || tmpl === 'track-backup';
      const isCm = data.size_unit === 'cm' || props['_cm'] === '1';
      const unitLabel = isCm ? 'cm' : 'inches';
      const width = getEditableWidthValue(props, data);
      const length = getEditableLengthValue(props, data);
      const ranges = getActiveSizeRanges(data, props);
      const rangeHint = ranges.length
        ? `<p class="cdv2-dim-range-hint">Allowed: width ${rangeForUnit(ranges[0] || '', isCm) || '—'}${ranges[1] ? `, length ${rangeForUnit(ranges[1], isCm)}` : ''} (${unitLabel})</p>`
        : '';
      const roomBefore = caps.room && (data.room_placement === 'before_dims');
      const roomBlock = this._renderRoomWithinSize(props, data, caps);
      const roomAfter = caps.room && (data.room_placement !== 'before_dims');

      // Rex (custom-track-rex): Width Type selector + X/Y/Z inputs
      if (isRexTrack(data)) {
        return this._renderRexDimensionGroup(props, data, caps, isCm, unitLabel, roomBefore, roomAfter, roomBlock, ranges);
      }

      // Track-joe fraction (inch only): 2-column — Width | Eighths
      if (usesTrackJoeFractionSelects(data, props)) {
        const mkOpts = () => FRACTION_OPTIONS.map(o =>
          `<option value="${o}"${getFractionSelectValue(props, 'width') === o ? ' selected' : ''}>${o === '0/0' ? '0' : o}</option>`
        ).join('');
        return `
          <div class="cdv2-edit-group" data-group="dimensions">
            <div class="cdv2-edit-label">
              Track Width
              <span class="cdv2-edit-label-sub">Width, ${unitLabel}</span>
            </div>
            ${roomBefore ? roomBlock : ''}
            <div class="cdv2-edit-grid cdv2-edit-grid--track-joe">
              <div class="cdv2-dim-col">
                <label class="cdv2-dim-col-label" for="cdv2-width">Width</label>
                <input type="text" id="cdv2-width" value="${this._esc(String(width))}" inputmode="numeric" autocomplete="off" data-dim="width">
              </div>
              <div class="cdv2-dim-col">
                <label class="cdv2-dim-col-label" for="cdv2-width-frac">Eighths</label>
                <select id="cdv2-width-frac" data-dim-frac="width" aria-label="Width eighths">${mkOpts()}</select>
              </div>
            </div>
            <p class="cdv2-dim-error" data-dim-error="width" hidden></p>
          </div>
          ${roomAfter ? `<div class="cdv2-edit-group" data-group="room">${roomBlock}</div>` : ''}
        `;
      }

      // Shades fraction selects: 4-column flat row — Width | Eighths | Length | Eighths
      if (usesShadeFractionSelects(data, props)) {
        const mkOpts = (dim) => FRACTION_OPTIONS.map(o =>
          `<option value="${o}"${getFractionSelectValue(props, dim) === o ? ' selected' : ''}>${o === '0/0' ? '0' : o}</option>`
        ).join('');
        const grid = `
          <div class="cdv2-edit-grid cdv2-edit-grid--shade">
            <div class="cdv2-dim-col">
              <label class="cdv2-dim-col-label" for="cdv2-width">Width</label>
              <input type="text" id="cdv2-width" value="${this._esc(String(width))}" inputmode="numeric" autocomplete="off" data-dim="width">
            </div>
            <div class="cdv2-dim-col">
              <label class="cdv2-dim-col-label" for="cdv2-width-frac">Eighths</label>
              <select id="cdv2-width-frac" data-dim-frac="width" aria-label="Width eighths">${mkOpts('width')}</select>
            </div>
            <div class="cdv2-dim-col">
              <label class="cdv2-dim-col-label" for="cdv2-length">Length</label>
              <input type="text" id="cdv2-length" value="${this._esc(String(length))}" inputmode="numeric" autocomplete="off" data-dim="length">
            </div>
            <div class="cdv2-dim-col">
              <label class="cdv2-dim-col-label" for="cdv2-length-frac">Eighths</label>
              <select id="cdv2-length-frac" data-dim-frac="length" aria-label="Length eighths">${mkOpts('length')}</select>
            </div>
          </div>
          <p class="cdv2-dim-error" data-dim-error="width" hidden></p>
          <p class="cdv2-dim-error" data-dim-error="length" hidden></p>`;

        return `
          <div class="cdv2-edit-group" data-group="dimensions">
            <div class="cdv2-edit-label">
              Size
              <span class="cdv2-edit-label-sub">Width × Length, ${unitLabel}</span>
            </div>
            ${roomBefore ? roomBlock : ''}
            ${grid}
            ${roomAfter ? roomBlock : ''}
          </div>
        `;
      }

      // Non-shades: standard 2-column layout (or 1-column for track)
      // Step label: read from whichever display field is stored in props (Track Width / Rod Width)
      const trackDisplayKey = ('Rod Width' in props) ? 'Rod Width'
        : ('Track Width' in props) ? 'Track Width'
        : (tmpl === 'custom-track-roman' ? 'Rod Width' : 'Track Width');
      const stepLabel = isTrack ? trackDisplayKey : 'Size';
      const fieldLabel = isTrack ? trackDisplayKey : 'Width';
      const widthField = this._renderDimFieldRow('width', fieldLabel, width, data, props, 'width');
      const lengthField = !isTrack ? this._renderDimFieldRow('length', 'Length', length, data, props, 'length') : '';
      const gridClass = isTrack ? 'cdv2-edit-grid cdv2-edit-grid--track' : 'cdv2-edit-grid';
      // Initial selected-size label (mirrors PDP step header display, e.g. "44"W x 66"L")
      const unit = isCm ? 'cm ' : '"';
      const initSizeLabel = isTrack
        ? (width !== '' ? `${width}${unit}` : '')
        : (width !== '' && length !== '' ? `${width}${unit}W x ${length}${unit}L` : '');

      // Depth field (custom-valance / custom-swag): mirrors v11 showDeepis — visible when Mount Type includes 'Velcro for Board Mounted'
      const DEPTH_OPTS = ['0"', '4"', '6"'];
      const isValanceOrSwag = tmpl === 'custom-valance' || tmpl === 'custom-swag';
      const currentMountType = props['Mount Type'] || '';
      const depthVisible = isValanceOrSwag && currentMountType.includes('Velcro for Board Mounted');
      const currentDepth = props['Depth'] || DEPTH_OPTS[0];
      const depthBlock = isValanceOrSwag ? `
        <div class="cdv2-edit-field" data-depth-row${depthVisible ? '' : ' hidden'}>
          <label for="cdv2-depth">Depth</label>
          <select id="cdv2-depth" data-depth-select>
            ${DEPTH_OPTS.map(o => `<option value="${this._esc(o)}"${o === currentDepth ? ' selected' : ''}>${this._esc(o)}</option>`).join('')}
          </select>
        </div>
      ` : '';

      return `
        <div class="cdv2-edit-group" data-group="dimensions">
          <div class="cdv2-edit-label">
            ${stepLabel}
            <span id="cdv2-dimensions-selected">${this._esc(initSizeLabel)}</span>
          </div>
          ${roomBefore ? roomBlock : ''}
          <div class="${gridClass}">
            ${widthField}
            ${lengthField}
          </div>
          ${depthBlock}
        </div>
        ${roomAfter ? `<div class="cdv2-edit-group" data-group="room">${roomBlock}</div>` : ''}
      `;
    }

    /* ── Rex (custom-track-rex) Width Type + X/Y/Z dimension group ── */
    _renderRexDimensionGroup(props, data, caps, isCm, unitLabel, roomBefore, roomAfter, roomBlock, ranges) {
      const widthTypes = data.width_types || [];
      const currentParam = props._width_type || '';
      const currentTitle = props['Width Type'] || '';
      const wtIndex = getRexWidthTypeIndex(currentTitle);

      // Image cards (3 per row) for Width Type — matches standard opt-card style
      const wtCards = widthTypes.map(wt => {
        const idx = getRexWidthTypeIndex(wt.title);
        const isSel = wt.param === currentParam;
        return `
          <button type="button" class="cdv2-opt-card${isSel ? ' is-selected' : ''}"
            data-width-type-param="${this._esc(wt.param)}"
            data-width-type-title="${this._esc(wt.title)}"
            data-width-type-index="${idx}">
            <span class="cdv2-opt-visual" style="${wt.image_url ? `background-image:url('${this._esc(wt.image_url)}');background-size:cover;` : ''}"></span>
            <strong>${this._esc(wt.title)}</strong>
          </button>`;
      }).join('');

      // Convert stored inches to display unit
      const inToDisp = (n) => {
        const v = parseFloat(n);
        if (Number.isNaN(v) || !v) return '';
        return isCm ? parseFloat((v / 0.393701).toFixed(1)) : parseFloat(v.toFixed(1));
      };

      // Parse original cm value for key (X/Y/Z) from Rod Width display string to avoid
      // inch round-trip precision loss (PDP stores "765.0cm (X=342, Y=423)" with original integers)
      const rodWidthStr = props['Rod Width'] || '';
      const parseRodWidthCm = (key) => {
        if (!isCm) return null;
        // Single window: "342cm" — no key prefix; only relevant for x
        if (key === 'x' && wtIndex === 1) {
          const m = rodWidthStr.match(/^([\d.]+)cm$/);
          return m ? parseFloat(m[1]) : null;
        }
        const re = new RegExp(`${key.toUpperCase()}=([\\d.]+)`);
        const m = rodWidthStr.match(re);
        return m ? parseFloat(m[1]) : null;
      };

      // Normalise _x/_y/_z to 1-decimal-place inches (matches PDP storage).
      // When in cm mode, reconstruct from the Rod Width display string to avoid
      // inch-round-trip error (44cm → 17.3in → 43.9cm).
      ['x', 'y', 'z'].forEach(k => {
        const cmVal = isCm ? parseRodWidthCm(k) : null;
        if (cmVal !== null) {
          props[`_${k}`] = parseFloat((cmVal * 0.393701).toFixed(1));
        } else if (props[`_${k}`] != null) {
          props[`_${k}`] = parseFloat(parseFloat(props[`_${k}`]).toFixed(1));
        }
      });

      // X/Y/Z input rows — only show those required by current width type
      const mkInput = (key, label, minIdx) => {
        const hidden = wtIndex < minIdx;
        const val = parseRodWidthCm(key) ?? inToDisp(props[`_${key}`]);
        return `
          <div class="cdv2-dim-xyz-row" data-type-min="${minIdx}"${hidden ? ' style="display:none"' : ''}>
            <label class="cdv2-dim-xyz-label">${label}</label>
            <div class="cdv2-dim-xyz-field">
              <input type="text" class="cdv2-dim-xyz-input" inputmode="decimal" autocomplete="off"
                data-dim-xyz="${key}" value="${this._esc(String(val))}">
              <span class="cdv2-dim-xyz-unit">${unitLabel}</span>
            </div>
          </div>`;
      };

      const rangeHint = ranges.length && ranges[0]
        ? `<p class="cdv2-dim-range-hint">Allowed: ${rangeForUnit(ranges[0], isCm) || '—'} (${unitLabel})</p>`
        : '';

      const rodWidthSection = wtIndex > 0 ? `
        <div class="cdv2-edit-group" data-group="rod-width">
          <div class="cdv2-edit-label">
            Rod Width
            <span id="cdv2-dimensions-selected">${this._esc(rodWidthStr)}</span>
          </div>
          <div class="cdv2-rex-dim-inputs">
            ${mkInput('x', 'X', 1)}
            ${mkInput('y', 'Y', 2)}
            ${mkInput('z', 'Z', 3)}
          </div>
          <p class="cdv2-dim-error" data-dim-error="width" hidden></p>
          ${roomAfter ? roomBlock : ''}
        </div>` : `
        <div class="cdv2-edit-group" data-group="rod-width">
          <p class="cdv2-dim-error" data-dim-error="width" hidden></p>
          ${roomAfter ? roomBlock : ''}
        </div>`;

      return `
        <div class="cdv2-edit-group" data-group="dimensions">
          ${roomBefore ? roomBlock : ''}
          <div class="cdv2-edit-label">
            Width Type
            <span id="cdv2-width-type-selected">${this._esc(currentTitle)}</span>
          </div>
          <div class="cdv2-card-grid cols-4" data-card-type="width_type">
            ${wtCards || '<span>—</span>'}
          </div>
        </div>
        ${rodWidthSection}
      `;
    }

    /* ── Quantity group ──────────────────────────────────────── */
    _renderQtyGroup(qty) {
      return `
        <div class="cdv2-edit-group" data-group="qty">
          <div class="cdv2-edit-label">Quantity</div>
          <div class="cdv2-edit-stepper">
            <button type="button" class="cdv2-stepper-minus" aria-label="Decrease quantity" ${qty <= 1 ? 'disabled' : ''}>
              ${ICON.minus}
            </button>
            <strong id="cdv2-qty-val">${qty}</strong>
            <button type="button" class="cdv2-stepper-plus" aria-label="Increase quantity">
              ${ICON.plus}
            </button>
          </div>
        </div>
      `;
    }

    /* ── Filter images by color label ── */
    _getImagesForColor(colorLabel, data) {
      if (!data) return [];
      // Use pre-grouped data from liquid (images_by_color) — handles large catalogs efficiently
      if (data.images_by_color && data.images_by_color[colorLabel]?.length) {
        return data.images_by_color[colorLabel];
      }
      // Fallback: filter flat images array by alt-tag (mirrors PDP logic)
      if (!data.images || !data.images.length) return [];
      const allColorLabels = (data.colors || []).map(c => c.label);
      const matched = [];
      const generic = [];
      data.images.forEach(img => {
        const alt = (img.alt || '').trim();
        const altParts = alt.split(',').map(s => s.trim());
        const firstAlt = altParts[0];
        const isGeneric = alt === data.title ||
          (!allColorLabels.some(cl => cl.trim() === firstAlt) && altParts[1]?.trim() !== data.title);
        if (isGeneric) generic.push(img);
        else if (altParts.some(p => p === colorLabel)) matched.push(img);
      });
      return matched.length ? [...matched, ...generic] : generic;
    }

    /* ── Rebuild media panel with a given image list ── */
    _updateMediaPanel(images, title) {
      const thumbsEl = $('#cdv2-editor-thumbs', this.panel);
      const mainEl = $('#cdv2-editor-main', this.panel);
      if (!thumbsEl || !mainEl || !images || !images.length) return;

      thumbsEl.innerHTML = images.map((img, i) => `
        <button class="cdv2-editor-thumb ${i === 0 ? 'is-active' : ''}"
          data-img-src="${img.src || ''}"
          style="background-image:url('${img.src || ''}');"
          aria-label="${img.alt || 'Product image ' + (i + 1)}"
          type="button"
        ></button>
      `).join('');

      const mainImg = mainEl.querySelector('img');
      if (mainImg && images[0]) {
        mainImg.src = images[0].src;
        mainImg.alt = images[0].alt || title;
      }

      this._bindThumbs();
    }

    /* ── Bind thumbnail switcher ───────────────────────────── */
    _bindThumbs() {
      const thumbs = $$('.cdv2-editor-thumb', this.panel);
      const mainImg = $('#cdv2-editor-main', this.panel);
      if (!mainImg) return;

      thumbs.forEach(btn => {
        btn.addEventListener('click', () => {
          thumbs.forEach(t => t.classList.remove('is-active'));
          btn.classList.add('is-active');
          const src = btn.dataset.imgSrc;
          const img = mainImg.querySelector('img');
          if (img) { img.src = src; }
          else { mainImg.style.backgroundImage = `url('${src}')`; }
        });
      });
    }

    /* ── Bind all interactive fields ──────────────────────── */
    _bindFields(data, caps) {
      const form = this.scroll;
      if (!form) return;

      if (this._fieldsAbort) this._fieldsAbort.abort();
      this._fieldsAbort = new AbortController();
      const { signal } = this._fieldsAbort;

      // Color swatches
      form.addEventListener('click', (e) => {
        const btn = e.target.closest('.cdv2-color-opt:not([disabled])');
        if (!btn) return;
        $$('.cdv2-color-opt', form).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const varId = parseInt(btn.dataset.variantId, 10);
        // Multi-option products: preserve non-color options when switching color
        if (this.activeData && this.activeData.all_variants && this.activeData.all_variants.length) {
          const colorIdx = this.activeData.color_option_index ?? 0;
          const colorVariant = this.activeData.all_variants.find(v => v.id === varId);
          if (colorVariant) {
            const currentOpts = this._getCurrentVariantOptions(this.activeData);
            currentOpts[colorIdx] = colorVariant.opts[colorIdx];
            const found = this._findVariantByOptions(this.activeData, currentOpts);
            this.pendingChanges.variantId = found ? found.id : varId;
          } else {
            this.pendingChanges.variantId = varId;
          }
        } else {
          this.pendingChanges.variantId = varId;
        }
        const lbl = $('#cdv2-color-selected', form);
        if (lbl) lbl.textContent = btn.dataset.colorLabel || '';

        // Update images to match selected color
        this._updateMediaPanel(this._getImagesForColor(btn.dataset.colorLabel || '', this.activeData), this.activeData?.title || '');

        this._markDirty();
      }, { signal });

      // Variant option buttons (pillow-cover: Size, etc.) — change variant while preserving other options
      form.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-variant-opt-index]');
        if (!btn || !this.activeData) return;
        const optIndex = parseInt(btn.dataset.variantOptIndex, 10);
        const optValue = btn.dataset.variantOptValue;
        form.querySelectorAll(`[data-variant-opt-index="${optIndex}"]`).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const lbl = form.querySelector(`#cdv2-vopt-${optIndex}-selected`);
        if (lbl) lbl.textContent = optValue;
        const currentOpts = this._getCurrentVariantOptions(this.activeData);
        currentOpts[optIndex] = optValue;
        const found = this._findVariantByOptions(this.activeData, currentOpts);
        if (found) this.pendingChanges.variantId = found.id;
        this._markDirty();
      }, { signal });

      // Option cards (linings, mounts, finials, swag) + sub-options — header is read-only
      form.addEventListener('click', (e) => {
        const card = e.target.closest('.cdv2-opt-card');
        if (!card) return;
        const type = card.dataset.optType;
        if (!type) return;

        const title = card.dataset.optTitle;
        const param = card.dataset.optParam;
        const isSub = type.endsWith('-sub');
        const parentType = isSub ? type.replace('-sub', '') : type;

        // Deselect siblings of same type
        $$(`.cdv2-opt-card[data-opt-type="${type}"]`, form).forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');

        if (!isSub) {
          // Selecting (or re-tapping) a parent always resets its sub-option in the
          // properties, so drop the old sub highlight too. Without this the previous
          // sub still looks selected while the stored value is parent-only — the
          // customer saves what looks like "Three Side / Black" and gets "Three Side".
          $$(`.cdv2-opt-card[data-opt-type="${type}-sub"]`, form)
            .forEach(c => c.classList.remove('is-selected'));

          // Parent card: toggle sub-panel visibility
          const hasSub = card.dataset.hasSub === 'true';
          $$('.cdv2-sub-panel', form).forEach(sp => {
            // Only toggle sub-panels that belong to THIS option group (same parentType)
            const siblingTypes = $$(`.cdv2-opt-card[data-opt-type="${type}"]`, form)
              .map(c => c.dataset.optParam);
            if (siblingTypes.includes(sp.dataset.parentParam)) {
              sp.style.display = sp.dataset.parentParam === param ? '' : 'none';
            }
          });

          // Update label with selected title
          const lbl = $(`#cdv2-${parentType}-selected`, form);
          if (lbl) lbl.textContent = title;
        } else {
          // Sub card: update label to show "Parent(Sub)"
          const parentParam = card.dataset.parentParam || '';
          const parentCard = form.querySelector(`.cdv2-opt-card[data-opt-type="${parentType}"][data-opt-param="${parentParam}"]`);
          const parentTitle = parentCard ? parentCard.dataset.optTitle : parentParam;
          const lbl = $(`#cdv2-${parentType}-selected`, form);
          if (lbl) lbl.textContent = `${parentTitle}(${title})`;
        }

        // Update pending changes properties
        this._applyOptionSelection(type, title, param, data);
        if (type === 'extra-runner_type' || type === 'extra-runner_type-sub') {
          this._applyRexMountingConstraint();
        }
        // Re-enable save when user picks a new mounting type after auto-clear
        if (type === 'extra-mounting_type') {
          this._requiredOptionsValid = true;
        }
        this._markDirty();
        // Block saving while a parent with sub-options has no sub picked yet.
        this._applySubSelectionUI();
      }, { signal });

      // Dimension inputs — keypress guard: digits only for integers, 1 decimal max for decimals
      form.addEventListener('keypress', (e) => {
        const input = e.target.closest('[data-dim]');
        if (!input || !this.activeData) return;
        const charCode = e.which || e.keyCode;
        if (charCode <= 31) return; // allow control chars (backspace handled by keydown)

        if (input.dataset.dimInput === 'pdp-number') {
          // Allow digits; allow one '.' only if none already present
          const isDot = charCode === 46;
          const isDigit = charCode >= 48 && charCode <= 57;
          if (!isDigit && !isDot) { e.preventDefault(); return; }
          if (isDot && input.value.includes('.')) { e.preventDefault(); return; }
          return;
        }

        if (dimIntegersOnly(this.activeData, this.pendingChanges.properties)) {
          if (charCode < 48 || charCode > 57) e.preventDefault();
        }
      }, { signal });

      const onDimFieldChange = (input) => {
        if (!input || !this.activeData) return;
        const dim = input.dataset.dim;
        const ad = this.activeData;
        const props = this.pendingChanges.properties;

        if (input.dataset.dimInput === 'pdp-number') {
          applyPdpValidateNumber(input, props, dim, ad);
          this._markDirty();
          return;
        }

        const sanitized = sanitizeDimInput(input.value, ad, props);

        if (sanitized === '' && input.value.trim() === '') {
          if (dim === 'width') {
            delete props._width;
            delete props._maxwidth;
            delete props._track_width;
            delete props.Width;
            delete props['Track Width'];
            delete props['Rod Width'];
          } else {
            delete props._length;
            delete props._maxlength;
            delete props.Length;
          }
          this._markDirty();
          return;
        }

        if (sanitized === '') return;

        input.value = String(sanitized);
        const numVal = Number(sanitized);
        if (Number.isNaN(numVal)) return;

        syncDimensionProps(props, ad, dim, numVal);
        this._markDirty();
      };

      form.addEventListener('input', (e) => {
        const input = e.target.closest('[data-dim]');
        if (input) onDimFieldChange(input);
      }, { signal });

      form.addEventListener('change', (e) => {
        const fracSel = e.target.closest('[data-dim-frac]');
        if (fracSel && this.activeData) {
          applyFractionChange(
            this.pendingChanges.properties,
            this.activeData,
            fracSel.dataset.dimFrac,
            fracSel.value
          );
          this._markDirty();
          return;
        }
        const depthSel = e.target.closest('[data-depth-select]');
        if (depthSel && this.activeData) {
          this.pendingChanges.properties['Depth'] = depthSel.value;
          this._markDirty();
          return;
        }
        const input = e.target.closest('[data-dim]');
        if (input) onDimFieldChange(input);
      }, { signal });

      // Rex Width Type selector
      form.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-width-type-param]');
        if (!btn || !this.activeData) return;
        const param = btn.dataset.widthTypeParam;
        const title = btn.dataset.widthTypeTitle;
        const wtIdx = parseInt(btn.dataset.widthTypeIndex, 10) || 1;
        const props = this.pendingChanges.properties;
        const rexIsCm = isDimCm(this.activeData, props);

        props._width_type = param;
        props['Width Type'] = title;

        // Update button selected state
        $$('[data-width-type-param]', form).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        // Sync step label
        const wtLbl = form.querySelector('#cdv2-width-type-selected');
        if (wtLbl) wtLbl.textContent = title;

        // Show/hide XYZ inputs based on type index; clear hidden values
        $$('[data-type-min]', form).forEach(row => {
          const min = parseInt(row.dataset.typeMin, 10);
          row.style.display = min <= wtIdx ? '' : 'none';
          if (min > wtIdx) {
            const xyzInput = row.querySelector('[data-dim-xyz]');
            if (xyzInput) { delete props[`_${xyzInput.dataset.dimXyz}`]; xyzInput.value = ''; }
          }
        });

        syncRexDimProps(props, rexIsCm, rexIsCm ? collectCmVals(form) : null);
        this._markDirty();
      }, { signal });

      // Rex X/Y/Z dimension inputs (step=0.1, mirrors PDP product-variants-v6)
      form.addEventListener('input', (e) => {
        const input = e.target.closest('[data-dim-xyz]');
        if (!input || !this.activeData) return;
        const key = input.dataset.dimXyz;
        const props = this.pendingChanges.properties;
        const rexIsCm = isDimCm(this.activeData, props);

        // Limit to 1 decimal place (mirrors PDP step="0.1")
        const sanitized = sanitizeDecimalInput(input.value);
        if (input.value !== sanitized) input.value = sanitized;

        const val = parseFloat(sanitized);
        if (!Number.isNaN(val) && val > 0) {
          // Store 1-decimal-place inches (matches PDP); use cmVals in syncRexDimProps to avoid round-trip error in display
          props[`_${key}`] = parseFloat((rexIsCm ? val * 0.393701 : val).toFixed(1));
        } else {
          delete props[`_${key}`];
        }
        syncRexDimProps(props, rexIsCm, rexIsCm ? collectCmVals(form) : null);
        this._markDirty();
      }, { signal });

      // Quantity stepper
      form.addEventListener('click', (e) => {
        const isMinus = e.target.closest('.cdv2-stepper-minus');
        const isPlus  = e.target.closest('.cdv2-stepper-plus');
        if (!isMinus && !isPlus) return;
        let q = this.pendingChanges.qty;
        if (isMinus) q = Math.max(1, q - 1);
        if (isPlus)  q = q + 1;
        this.pendingChanges.qty = q;
        const display = $('#cdv2-qty-val', form);
        if (display) display.textContent = q;
        const minusBtn = $('.cdv2-stepper-minus', form);
        if (minusBtn) minusBtn.disabled = q <= 1;
        this._markDirty();
      }, { signal });

      // Text fields (room name, etc.)
      form.addEventListener('input', (e) => {
        const input = e.target.closest('[data-text-key]');
        if (!input) return;
        const key = input.dataset.textKey;
        if (key === 'room_name') {
          this.pendingChanges.properties['Room Name'] = input.value;
          const roomErrEl = this.panel.querySelector('#cdv2-room-name-error');
          if (roomErrEl && input.value.trim()) roomErrEl.style.display = 'none';
        } else if (key === 'Bend Note') {
          this.pendingChanges.properties['Bend Note'] = input.value;
          const bendErrEl = this.panel.querySelector('#cdv2-bend-note-error');
          if (bendErrEl && input.value.trim()) bendErrEl.style.display = 'none';
        } else {
          this.pendingChanges.properties[key] = input.value;
        }
        this._markDirty();
      }, { signal });
    }

    /* ── Apply option selection to pending properties ───────── */
    _applyOptionSelection(type, title, param, data) {
      const props = this.pendingChanges.properties;

      switch (type) {
        case 'header':
          props['Header'] = title;
          props['_header'] = param;
          // Reset sub-header selection when parent changes
          delete props['_header_sub'];
          // Note: header is read-only in the cart editor, so there is no "clear the
          // lining when the header changes" step here. The lining swatch set is
          // filtered once at open time by _fLinings via window.LiningRules.
          break;
        case 'header-sub':
          props['_header_sub'] = param;
          // The full header display value becomes "Parent / Sub"
          // but we keep Header as parent for compatibility
          break;
        case 'lining':
          props['Lining'] = title;
          props['_lining'] = param;
          delete props['_lining_sub'];
          break;
        case 'lining-sub':
          props['_lining_sub'] = param;
          break;
        case 'mount':
          props['Mount Type'] = title;
          props['_mount_type'] = param;
          props['_mount'] = param;
          delete props['_mount_sub'];
          // 内装才有 1/4" 减宽：改成外装要把 Finished 两项删掉，改回内装要重新算出来
          syncShadeOrderedFinished(props, this.activeData);
          this._updateDepthVisibility();
          break;
        case 'mount-sub':
          props['_mount_sub'] = param;
          break;
        case 'finial':
          props['Finial Style'] = title;
          props['_finial'] = param;
          delete props['_finial_sub'];
          break;
        case 'finial-sub':
          props['_finial_sub'] = param;
          break;
        case 'swag':
          props['Swag Style'] = title;
          props['_swag'] = param;
          break;
        case 'controls':
          props['Lift Control'] = title;
          props['_lift_control'] = param;
          delete props['_controls_sub'];
          break;
        case 'controls-sub':
          props['_lift_control'] = param;
          break;
        case 'tieback':
          props['Tieback'] = title;
          props['_tieback'] = param;
          break;
        case 'design':
          props['Design'] = title;
          props['_design'] = param;
          break;
        case 'design-sub':
          // design has no subs, but handle defensively
          props['_design'] = param;
          break;
        default: {
          // Handle extra-{optId} and extra-{optId}-sub (custom family additional options)
          if (type.startsWith('extra-')) {
            const isSub = type.endsWith('-sub');
            const optId = (isSub ? type.slice(0, -4) : type).replace(/^extra-/, '');
            const group = this.activeData && (this.activeData.extra_options || []).find(g => g.option_id === optId);
            if (group) {
              // Both parent and sub write to prop_key; sub param is already the compound value
              props[group.prop_key] = param;
              // Rex: _track_type must mirror _rod_type (mirrors PDP v6 buildProperties)
              if (optId === 'rod_type') props._track_type = param;
              const existingDisplayKey = Object.keys(props).find(
                k => k.toLowerCase() === group.label.toLowerCase()
              );
              if (!isSub) {
                props[existingDisplayKey || group.label] = title;
              } else {
                // Sub click: find parent item to build display string
                const parentItem = group.items && group.items.find(it =>
                  it.subs && it.subs.some(s => s.param === param)
                );
                const parentTitle = parentItem ? parentItem.title : '';
                // runner_type uses "Parent(Sub)" format (matches PDP step header); others use "Parent - Sub"
                const displayVal = parentTitle
                  ? (optId === 'runner_type' ? `${parentTitle}(${title})` : `${parentTitle} - ${title}`)
                  : title;
                props[existingDisplayKey || group.label] = displayVal;
              }
            }
            break;
          }
          props[title] = param;
        }
      }
    }

    /* ── Escape HTML ─────────────────────────────────────────── */
    _esc(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  /* ── Cart Drawer V2 UI helpers (notes, collapse, badge) ─── */
  class CartDrawerV2UI {
    constructor() {
      this._bind();
      this._bindNoteToggle();
      document.addEventListener('cart:refreshed', () => this._bindNoteToggle());
    }

    _bind() {
      // Animate badge on first render
      const badge = $('#cdv2-badge');
      if (badge) {
        badge.classList.add('is-bouncing');
        badge.addEventListener('animationend', () => badge.classList.remove('is-bouncing'));
      }
    }

    _bindNoteToggle() {
      const trigger = document.getElementById('order-note-toggle');
      if (!trigger || trigger.__cdv2NoteToggleBound) return;
      trigger.__cdv2NoteToggleBound = true;
      trigger.addEventListener('click', () => {
        const noteWrap = trigger.closest('.cdv2-note');
        if (!noteWrap) return;
        const isOpen = noteWrap.dataset.open === 'true';
        noteWrap.dataset.open = String(!isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (!isOpen) {
          const ta = document.getElementById('mini-cart__notes');
          const counter = noteWrap.querySelector('.cdv2-note-count');
          if (ta && counter) {
            const max = parseInt(ta.getAttribute('maxlength') || '500', 10);
            counter.textContent = `${ta.value.length} / ${max}`;
          }
        }
      });
    }
  }

  /* ── Init ─────────────────────────────────────────────────── */
  function init() {
    new CartDrawerV2UI();
    bindCartEditorDocumentEvents();
    window.cartEditorV2 = new CartEditor();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
